import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  db, auth, storage, functions,
  collection, query, where, getDocs, getDoc, doc, setDoc, updateDoc, deleteDoc, onSnapshot, addDoc, limit, orderBy,
  ref, uploadBytesResumable, getDownloadURL,
  httpsCallable, isMockMode
} from '../firebase';
import { UploadWorkerPool, withExponentialBackoff } from './UploadWorkerPool';
import { 
  FolderPlus, Folder, Loader2, ArrowRight, ArrowLeft,
  Upload, FileText, CheckCircle2, AlertCircle, Trash2, 
  ChevronLeft, ChevronRight, Save, Download, FileSpreadsheet,
  LogOut, Shield, ShieldAlert, User, Sparkles, Image as ImageIcon, Check, RefreshCw, Edit2, GripVertical, X, MessageSquare, Mic as MicIcon
} from 'lucide-react';
import UploadZone from './UploadZone';
import Header from './Header';
import AutoSaveIndicator from './AutoSaveIndicator';
import { AuthProvider } from '@/context/AuthContext';
import { UpgradeModal } from './UpgradeModal';
import { FeedbackModal } from './FeedbackModal';
import { aiGrammarCheck, aiObservationAssessor, aiGenerateRecommendation, aiTranslateAndGrammarCheck, generateRecommendation, getAISuggestions, learnComment } from '../aiAssessor';
import AnnotatedImageCanvas from './AnnotatedImageCanvas';
import { handleExportWord, getBestPhotoBase64 } from '../exportWordReport';
import PublishBar from './PublishBar';
import { compressImage } from '../imageCompressor';
import { shareFile } from '../utils/shareFile';
import { useProjectAutoSave } from '@/hooks/useProjectAutoSave';
import PhotoItem from './PhotoItem';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { updateSessionActiveProject } from '../sessionSecurity';

// Local Cache Helpers (24-hour expiry)
const getProjectsCacheKey = (user) => `hitecmedia_projects_cache_${(user?.email || '').trim().toLowerCase()}`;

const loadProjectsFromCache = (user) => {
  if (!user || !user.email) return null;
  try {
    const raw = localStorage.getItem(getProjectsCacheKey(user));
    if (!raw) return null;
    const data = JSON.parse(raw);
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    if (!data.timestamp || (Date.now() - data.timestamp) > TWENTY_FOUR_HOURS) {
      return null;
    }
    return Array.isArray(data.projects) ? data.projects : null;
  } catch (e) {
    return null;
  }
};

const saveProjectsToCache = (user, projectsList) => {
  if (!user || !user.email || !Array.isArray(projectsList)) return;
  try {
    localStorage.setItem(getProjectsCacheKey(user), JSON.stringify({
      timestamp: Date.now(),
      projects: projectsList
    }));
  } catch (e) {
    console.error("Error saving projects LocalStorage cache:", e);
  }
};

export const getLocalTodayStr = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function Dashboard({ user, onLogout, onOpenSecurity }) {
  // Mobile Tab State: 'upload' | 'editor' | 'export'
  const [activeTab, setActiveTab] = useState('upload');
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobileUser = isMobileViewport && user?.role === 'user';
  const isMobileMode = (user?.viewMode || localStorage.getItem('hitec_view_mode') || 'Mobile') === 'Mobile';

  // State to auto-collapse Company/City and Project sections on mobile to optimize screen space
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(() => {
    const mobileMode = (user?.viewMode || localStorage.getItem('hitec_view_mode') || 'Mobile') === 'Mobile';
    return mobileMode || window.innerWidth < 768;
  });



  const handleTouchStart = (e) => {
    if (e.targetTouches && e.targetTouches[0]) {
      setTouchStartX(e.targetTouches[0].clientX);
      setTouchStartY(e.targetTouches[0].clientY);
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null || touchStartY === null || !e.changedTouches || !e.changedTouches[0]) return;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStartX - touchEndX;
    const diffY = Math.abs(touchStartY - touchEndY);

    if (Math.abs(diffX) > 50 && Math.abs(diffX) > diffY) {
      if (diffX > 0 && (activeTab === 'upload' || activeTab === 'export')) {
        setActiveTab('editor');
      } else if (diffX < 0 && activeTab === 'editor') {
        setActiveTab('upload');
      }
    }
    setTouchStartX(null);
    setTouchStartY(null);
  };
  
  // Projects state
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const hasProject = Boolean(selectedProject?.id);

  // RULE 1 & 2: Auto expand/collapse header based on project existence (mobile only)
  useEffect(() => {
    if (!hasProject) {
      setIsHeaderCollapsed(false); // Force Expand when no project
    } else if (isMobileMode || isMobileViewport) {
      setIsHeaderCollapsed(true); // Auto collapse when project exists on mobile
    }
  }, [hasProject, isMobileMode, isMobileViewport]);

  const [newProjectName, setNewProjectName] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Daily upload limit state
  const [dailyUploadCount, setDailyUploadCount] = useState(0);
  const [planLimits, setPlanLimits] = useState({ maxDaily: 100, maxKb: 300 }); // starter defaults
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [connectivityModal, setConnectivityModal] = useState(null);
  const [mobileShareModal, setMobileShareModal] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  // Upload Queue state (remember queue per project)
  const [projectQueues, setProjectQueues] = useState({});
  const queue = selectedProject ? (projectQueues[selectedProject.id] || []) : [];

  const setQueue = (updater) => {
    if (!selectedProject) return;
    setProjectQueues(prev => {
      const currentQueue = prev[selectedProject.id] || [];
      const newQueue = typeof updater === 'function' ? updater(currentQueue) : updater;
      return {
        ...prev,
        [selectedProject.id]: newQueue
      };
    });
  };
  
  // Data retention window: 7 days for shared demo/hitec.id accounts, 3 days otherwise
  const retentionDays = (() => {
    const email = (user?.email || '').trim().toLowerCase();
    if (email === 'demo@hitec.id' || email.endsWith('@hitec.id')) return 7;
    return 3;
  })();
  const retentionMs = retentionDays * 24 * 60 * 60 * 1000;

  // Photo Editor state
  const [projectPhotos, setProjectPhotos] = useState([]);
  const [editorIndex, setEditorIndex] = useState(0);

  // Sync active project state to session monitoring
  useEffect(() => {
    if (user && user.token) {
      if (selectedProject) {
        updateSessionActiveProject(
          user.token, 
          selectedProject.id, 
          selectedProject.name, 
          selectedProject.company_name, 
          selectedProject.city_name
        );
      } else {
        updateSessionActiveProject(user.token, '', 'Dashboard Menu', '', '');
      }
    }
  }, [selectedProject?.id, selectedProject?.name, selectedProject?.company_name, selectedProject?.city_name, user]);

  // Global Watchdog: automatically remove any photo stuck at 100% loading indicator or stuck processing indefinitely
  useEffect(() => {
    if (!queue || queue.length === 0) return;
    const interval = setInterval(() => {
      const now = Date.now();
      let hasStuck = false;
      const cleanQueue = queue.filter(item => {
        if (item.status === 'Done' || item.status === 'Rejected') return true;
        const started = item.startedAt || item.queuedAt || now;
        // If stuck at 100% for over 20 seconds, or stuck Uploading for over 35 seconds, auto-remove
        if (item.progress === 100 && (now - (item.reached100At || started)) > 20000) {
          console.warn('[Global Watchdog] Auto-removing photo stuck at 100% loading:', item.finalFilename);
          hasStuck = true;
          return false;
        }
        if (item.status === 'Uploading' && (now - started) > 35000) {
          console.warn('[Global Watchdog] Auto-removing photo stuck processing:', item.finalFilename);
          hasStuck = true;
          return false;
        }
        return true;
      });
      if (hasStuck) {
        setQueue(cleanQueue);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [queue]);
  const [caption, setCaption] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);

  // Fire Safety Assessor UI State
  const [commentsText, setCommentsText] = useState('');
  const [commentsLang, setCommentsLang] = useState('EN');
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLang, setRecommendationsLang] = useState('EN');
  const [aiGrammarChecking, setAiGrammarChecking] = useState(false);
  const [aiGeneratingRec, setAiGeneratingRec] = useState(false);
  const [photoGrade, setPhotoGrade] = useState('F2');
  const [photoStatus, setPhotoStatus] = useState('Open');
  const [savingGrade, setSavingGrade] = useState(false);
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoDate, setPhotoDate] = useState('');
  const [photoLocation, setPhotoLocation] = useState('');
  const [recMode, setRecMode] = useState('Auto'); // 'Auto' | 'Manual'
  const [aiAssistOn, setAiAssistOn] = useState(true);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestedRecText, setAiSuggestedRecText] = useState('');

  const { autosave, isSaving, isError, lastSavedAt } = useProjectAutoSave(selectedProject?.id);

  const updatePhotoFieldAndAutosave = (fieldUpdates) => {
    if (projectPhotos.length === 0 || editorIndex === null || !projectPhotos[editorIndex]) return;
    const currentPhoto = projectPhotos[editorIndex];
    const updatedPhoto = {
      ...currentPhoto,
      ...fieldUpdates
    };
    const updatedPhotos = projectPhotos.map((p, idx) => idx === editorIndex ? updatedPhoto : p);
    setProjectPhotos(updatedPhotos);
    if (currentPhoto.id) {
      // Always include project_id and expires_at so the photo stays bound to its project
      const expiresIso = new Date(Date.now() + retentionMs).toISOString();
      updateDoc(doc(db, 'photos', currentPhoto.id), {
        ...fieldUpdates,
        project_id: currentPhoto.project_id || selectedProject?.id || '',
        company_id: currentPhoto.company_id || user?.companyId || 'hitec',
        expires_at: expiresIso,
        lastModified: new Date().toISOString()
      }).catch(() => {});
    }
    autosave({
      photos: updatedPhotos,
      lastEditedPhotoId: currentPhoto.id,
      ...fieldUpdates
    });
  };

  const handleSaveReport = () => {
    if (projectPhotos.length === 0 || editorIndex === null || !projectPhotos[editorIndex]) return;
    const currentPhoto = projectPhotos[editorIndex];
    const updates = {
      caption: photoTitle || '',
      title: photoTitle || '',
      asset_title: photoTitle || '',
      date: photoDate || '',
      exif_date: photoDate || '',
      location: photoLocation || '',
      exif_gps: photoLocation || '',
      grade: photoGrade || 'F2',
      assessment_grade: photoGrade || 'F2',
      status: photoStatus || 'Open',
      latest_status: photoStatus || 'Open',
      comments_text: commentsText || '',
      comments: commentsText || '',
      recommendations_json: recommendations || [],
      aiSuggestedRec: aiSuggestedRecText || '',
      comments_lang: commentsLang || 'EN',
      recommendations_lang: recommendationsLang || 'EN',
      lastModifiedAt: Date.now()
    };
    updatePhotoFieldAndAutosave(updates);
  };

  const cardASpeech = useSpeechToText();

  useEffect(() => {
    if (cardASpeech.transcript && !cardASpeech.isRecording) {
      setCommentsText(cardASpeech.transcript);
      updatePhotoFieldAndAutosave({ comments_text: cardASpeech.transcript });
    }
  }, [cardASpeech.transcript, cardASpeech.isRecording]);

  const handleUpdateCaptionFromVoice = (photoIdOrFilename, newCaption) => {
    if (!photoIdOrFilename || newCaption === undefined || newCaption === null) return;
    setProjectPhotos(prev => {
      const updatedPhotos = prev.map(p => 
        (p.id && p.id === photoIdOrFilename) || p.filename === photoIdOrFilename ? { ...p, caption: newCaption } : p
      );
      if (selectedProject) {
        setSelectedProject(prevProj => prevProj ? { ...prevProj, photos: updatedPhotos } : null);
      }
      const targetPhoto = prev.find(p => (p.id && p.id === photoIdOrFilename) || p.filename === photoIdOrFilename);
      if (targetPhoto && targetPhoto.id) {
        updateDoc(doc(db, 'photos', targetPhoto.id), { caption: newCaption }).catch(() => {});
      }
      autosave({
        photos: updatedPhotos,
        lastEditedPhotoId: targetPhoto ? targetPhoto.id : null,
        caption: newCaption,
        captionSavedAt: Date.now()
      }, { immediate: true });
      return updatedPhotos;
    });
    const currentPhoto = projectPhotos[editorIndex];
    if (currentPhoto && ((currentPhoto.id && currentPhoto.id === photoIdOrFilename) || currentPhoto.filename === photoIdOrFilename)) {
      setPhotoTitle(newCaption);
    }
  };

  // Gemini AI bilingual spelling correction and suggestion helper (Bahasa Indonesia & English)
  const generateGeminiSuggestions = (input) => {
    const raw = (input || '').trim();
    if (!raw) {
      return [
        { tag: 'ID Baku', text: 'Inspeksi dan verifikasi instalasi sistem proteksi kebakaran lantai 1 selesai dengan baik.' },
        { tag: 'EN Report', text: 'Inspection and verification of 1st-floor fire safety installation completed successfully.' },
        { tag: 'Summary', text: 'Dokumentasi progres pekerjaan instalasi dan kelayakan sistem.' }
      ];
    }

    // Indonesian spelling correction dictionary & grammar cleanup
    let correctedID = raw
      .replace(/\bsulah\b/gi, 'sudah')
      .replace(/\bkarna\b/gi, 'karena')
      .replace(/\bpoto\b/gi, 'foto')
      .replace(/\bijin\b/gi, 'izin')
      .replace(/\btdk\b/gi, 'tidak')
      .replace(/\byg\b/gi, 'yang')
      .replace(/\bdgn\b/gi, 'dengan')
      .replace(/\bskrg\b/gi, 'sekarang')
      .replace(/\bdr\b/gi, 'dari')
      .replace(/\butk\b/gi, 'untuk')
      .replace(/\bkl\b/gi, 'kalau');

    correctedID = correctedID.charAt(0).toUpperCase() + correctedID.slice(1);
    if (!/[.!]$/.test(correctedID)) correctedID += '.';

    // Professional English technical report phrasing
    let enReport = raw;
    if (/pipa|kran|keran/i.test(raw)) {
      enReport = 'Installation and testing of water piping and faucet systems completed.';
    } else if (/apar|pemadam|kebakaran|fire/i.test(raw)) {
      enReport = 'Inspection and compliance verification of fire protection equipment.';
    } else if (/selesai|done|sudah/i.test(raw)) {
      enReport = `Completed work verification: ${raw.replace(/[.]$/, '')}.`;
    } else {
      enReport = `Technical field report: ${correctedID}`;
    }

    const polishedID = `Laporan Inspeksi: ${correctedID}`;

    return [
      { tag: 'ID Baku (Corrected)', text: correctedID },
      { tag: 'EN Technical Report', text: enReport },
      { tag: 'ID Formal Polish', text: polishedID }
    ];
  };

  const geminiSuggestionsMemo = useMemo(() => generateGeminiSuggestions(caption), [caption]);

  const runGeminiCorrection = () => {
    setAiProcessing(true);
    setTimeout(() => {
      const suggestions = generateGeminiSuggestions(caption);
      if (suggestions && suggestions[0]) {
        setCaption(suggestions[0].text);
      }
      setAiProcessing(false);
    }, 400);
  };

  const [exportingPPTX, setExportingPPTX] = useState(false);
  const [exportingXLSX, setExportingXLSX] = useState(false);
  const [exportingDOCX, setExportingDOCX] = useState(false);
  const [exportError, setExportError] = useState(null);

  // Custom testing selection states
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [confirmedExports, setConfirmedExports] = useState(false);
  const [alertPopup, setAlertPopup] = useState(null);
  const [companyName, setCompanyName] = useState(() => localStorage.getItem('hitec_company_name') || '');
  const [cityName, setCityName] = useState(() => localStorage.getItem('hitec_city_name') || '');
  const [fieldValidationErrors, setFieldValidationErrors] = useState({ company: false, city: false });

  useEffect(() => {
    setConfirmedExports(false);
  }, [projectPhotos.length, selectedProject?.id]);

  // Worker pool ref — persists across renders without causing re-renders
  const workerPoolRef = useRef(null);
  if (!workerPoolRef.current) {
    workerPoolRef.current = new UploadWorkerPool(6); // 6 simultaneous uploads
  }

  const togglePhotoSelection = (filename, status) => {
    if (status !== 'Done') return;
    setConfirmedExports(false);
    setSelectedPhotos(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  // Drag and Drop row reordering
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  const [dragOverItemIndex, setDragOverItemIndex] = useState(null);

  const handleDropQueueItem = (toIndex) => {
    if (draggedItemIndex === null || draggedItemIndex === toIndex) {
      setDraggedItemIndex(null);
      setDragOverItemIndex(null);
      return;
    }

    const fromIndex = draggedItemIndex;

    // 1. Reorder queue array
    setQueue(prevQueue => {
      const newQueue = [...prevQueue];
      const [movedItem] = newQueue.splice(fromIndex, 1);
      newQueue.splice(toIndex, 0, movedItem);
      return newQueue;
    });

    // 2. Reorder projectPhotos array so that its ordering stays in sync with the updated queue
    let reorderedPhotos = [...projectPhotos];
    setProjectPhotos(prevPhotos => {
      const newQueueItem = queue[fromIndex];
      const targetQueueItem = queue[toIndex];
      if (!newQueueItem || !targetQueueItem) return prevPhotos;

      const newPhotos = [...prevPhotos];
      const photoFromIdx = newPhotos.findIndex(p => p.filename === newQueueItem.finalFilename);
      if (photoFromIdx === -1) return prevPhotos;

      const [movedPhoto] = newPhotos.splice(photoFromIdx, 1);
      const photoToIdx = newPhotos.findIndex(p => p.filename === targetQueueItem.finalFilename);
      if (photoToIdx === -1) {
        newPhotos.push(movedPhoto);
      } else {
        newPhotos.splice(photoToIdx, 0, movedPhoto);
      }
      reorderedPhotos = newPhotos;
      if (selectedProject) {
        setSelectedProject(prev => prev ? { ...prev, photos: newPhotos } : null);
      }
      return newPhotos;
    });

    autosave({ photos: reorderedPhotos, reorderedAt: Date.now() }, { immediate: true });
    setDraggedItemIndex(null);
    setDragOverItemIndex(null);
  };

  const doneItems = queue.filter(i => i.status === 'Done');
  const allDoneSelected = doneItems.length > 0 && doneItems.every(i => selectedPhotos.includes(i.finalFilename));

  const handleSelectAll = () => {
    setConfirmedExports(false);
    if (allDoneSelected) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(doneItems.map(i => i.finalFilename));
    }
  };

  // Navigate to a specific photo in the Caption Editor (used when clicking the body of a queue item)
  const navigateToPhoto = (filename) => {
    const photoIndex = projectPhotos.findIndex(p => p.filename === filename);
    if (photoIndex === -1) return;
    setEditorIndex(photoIndex);
    setActiveTab('editor'); // switch mobile tab to editor
  };

  const [todayStr, setTodayStr] = useState(getLocalTodayStr);

  // Live ref always pointing to the current state — used in emergency flush closures
  const latestStateRef = useRef({ projectPhotos: [], selectedProject: null, user: null, retentionMs: 0 });
  useEffect(() => {
    latestStateRef.current = { projectPhotos, selectedProject, user, retentionMs };
  });

  // Emergency flush: persist ALL unsaved photo edits to Firestore on tab hide or page close.
  // This guarantees no data loss when an assessor closes the browser mid-edit.
  useEffect(() => {
    const flushAllPhotos = () => {
      const { projectPhotos: photos, selectedProject: proj, user: u, retentionMs: rms } = latestStateRef.current;
      if (!proj || !photos.length || !u) return;
      const nowIso = new Date().toISOString();
      const expiresIso = new Date(Date.now() + rms).toISOString();
      // Write each photo doc synchronously — fire-and-forget (no await in pagehide)
      photos.forEach(p => {
        if (!p.id) return;
        const payload = {
          project_id: proj.id,
          company_id: u.companyId || p.company_id || 'hitec',
          caption: p.caption || '',
          title: p.title || p.caption || '',
          asset_title: p.asset_title || p.caption || '',
          comments_text: p.comments_text || p.comments || '',
          comments: p.comments || p.comments_text || '',
          comments_lang: p.comments_lang || 'EN',
          grade: p.grade || p.assessment_grade || 'F2',
          assessment_grade: p.assessment_grade || p.grade || 'F2',
          latest_status: p.latest_status || p.status || 'Open',
          date: p.date || p.exif_date || '',
          location: p.location || '',
          recommendations_json: Array.isArray(p.recommendations_json) ? p.recommendations_json : [],
          recommendations_lang: p.recommendations_lang || 'EN',
          expires_at: expiresIso,
          lastModified: nowIso
        };
        // updateDoc is fire-and-forget — browsers may or may not complete it on pagehide
        updateDoc(doc(db, 'photos', p.id), payload).catch(() => {});
      });
      // Also flush the project doc with cleanPhotos
      const cleanPhotos = photos.map(p => ({
        id: p.id || '',
        filename: p.filename || '',
        url: p.url || '',
        base64: p.base64 || p.thumbnailUrl || '',
        thumbnailUrl: p.thumbnailUrl || p.base64 || p.url || '',
        caption: p.caption || '',
        comments_text: p.comments_text || p.comments || '',
        comments: p.comments || p.comments_text || '',
        grade: p.grade || 'F2',
        assessment_grade: p.assessment_grade || p.grade || 'F2',
        latest_status: p.latest_status || p.status || 'Open',
        date: p.date || '',
        location: p.location || '',
        recommendations_json: Array.isArray(p.recommendations_json) ? p.recommendations_json : [],
        expires_at: expiresIso,
        size_kb: p.size_kb || 0
      }));
      setDoc(doc(db, 'projects', proj.id), {
        photos: cleanPhotos,
        lastModified: nowIso,
        expires_at: expiresIso,
        retention_days: Math.round(rms / 86400000)
      }, { merge: true }).catch(() => {});
    };

    // Flush when tab is hidden (most reliable browser lifecycle hook)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushAllPhotos();
    };
    // Fallback: flush on page unload
    const handlePageHide = () => flushAllPhotos();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []); // empty deps — intentionally uses latestStateRef to avoid stale closure

  useEffect(() => {
    const checkMidnight = setInterval(() => {
      const currentLocalDay = getLocalTodayStr();
      if (currentLocalDay !== todayStr) {
        setTodayStr(currentLocalDay);
      }
    }, 30 * 1000);
    return () => clearInterval(checkMidnight);
  }, [todayStr]);

  // Fetch plan limit
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        // Query plan based on whitelist user company/plan
        const userWhitelistDoc = await getDocs(query(collection(db, 'whitelist_users'), where('email', '==', user.email.toLowerCase())));
        if (!userWhitelistDoc.empty) {
          const userData = userWhitelistDoc.docs[0].data();
          const planName = userData.plan || 'starter';
          
          const planSnap = await getDoc(doc(db, 'plan', planName));
          if (planSnap.exists()) {
            const p = planSnap.data();
            setPlanLimits({
              maxDaily: p.max_daily_photos || (planName === 'pro' ? 300 : 100),
              maxKb: p.max_file_size_kb || (planName === 'pro' ? 1024 : 300)
            });
          } else {
            // Default rules
            setPlanLimits({
              maxDaily: planName === 'pro' ? 300 : 100,
              maxKb: planName === 'pro' ? 1024 : 300
            });
          }
        }
      } catch (err) {
        console.error("Error fetching plan constraints:", err);
      }
    };
    fetchPlan();
  }, [user]);

  // Fetch projects: 1. Instant load from 24h LocalStorage cache; 2. Background Firestore sync
  useEffect(() => {
    if (!user || !user.email) return;

    // 1. Instant load from LocalStorage cache (if <= 24 hours old)
    const cachedProjs = loadProjectsFromCache(user);
    if (cachedProjs) {
      setProjects(cachedProjs);
      setLoadingProjects(false);
      setSelectedProject(prev => {
        if (prev && cachedProjs.some(p => p.id === prev.id)) {
          return cachedProjs.find(p => p.id === prev.id);
        }
        return cachedProjs.length > 0 ? cachedProjs[0] : null;
      });
    }

    // 2. Background Firestore sync
    const q = user.companyId
      ? query(collection(db, 'projects'), where('company_id', '==', user.companyId))
      : query(collection(db, 'projects'), where('created_by', '==', (user.email || '').trim().toLowerCase()));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const projs = [];
      const nowMs = Date.now();
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data.expires_at && new Date(data.expires_at).getTime() < nowMs) {
          return;
        }
        projs.push({
          id: docSnap.id,
          lastModified: new Date(0).toISOString(),
          ...data,
          photos: Array.isArray(data.photos)
            ? data.photos.filter(p => !p.expires_at || new Date(p.expires_at).getTime() >= nowMs)
            : []
        });
      });

      // Update state & refresh cache
      setProjects(projs);
      setLoadingProjects(false);
      saveProjectsToCache(user, projs);

      setSelectedProject(prev => {
        if (prev && projs.some(p => p.id === prev.id)) {
          return projs.find(p => p.id === prev.id);
        }
        return projs.length > 0 ? projs[0] : null;
      });
    });
    return () => unsubscribe();
  }, [user]);

  // Dynamic Monthly Report Downloads Counter: tracks generated reports (PDF, PPT, DOC) created in current calendar month
  const [monthlyReportCount, setMonthlyReportCount] = useState(0);
  const currentMonthStr = useMemo(() => {
    const d = new Date();
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${yy}-${mm}`;
  }, []);

  useEffect(() => {
    if (!user || !user.email) return;

    const userEmailClean = (user.email || '').trim().toLowerCase();
    const q = query(
      collection(db, 'report_downloads')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const uniqueReportIds = new Set();
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();

      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (data) {
          const matchUser = (data.user_email?.toLowerCase() === userEmailClean || data.userId === user.uid || data.user_id === user.uid);
          if (matchUser) {
            const dlTime = data.download_timestamp ? new Date(data.download_timestamp) : (data.created_at ? new Date(data.created_at) : null);
            const isSameMonth = dlTime
              ? (dlTime.getFullYear() === currentYear && dlTime.getMonth() === currentMonth)
              : (data.download_date && data.download_date.startsWith(currentMonthStr));

            if (isSameMonth) {
              uniqueReportIds.add(data.id || docSnap.id);
            }
          }
        }
      });

      // Also check local mock store
      try {
        const cached = localStorage.getItem('hitecmedia_mock_db');
        if (cached) {
          const store = JSON.parse(cached);
          if (Array.isArray(store.report_downloads)) {
            store.report_downloads.forEach(d => {
              if (d.user_email?.toLowerCase() === userEmailClean) {
                const dlTime = d.download_timestamp ? new Date(d.download_timestamp) : (d.created_at ? new Date(d.created_at) : null);
                const isSameMonth = dlTime
                  ? (dlTime.getFullYear() === currentYear && dlTime.getMonth() === currentMonth)
                  : (d.download_date && d.download_date.startsWith(currentMonthStr));

                if (isSameMonth) {
                  uniqueReportIds.add(d.id || `mock_${d.download_timestamp}`);
                }
              }
            });
          }
        }
      } catch (e) {}

      setMonthlyReportCount(uniqueReportIds.size);
    }, (err) => {
      console.warn("Report downloads listener warning:", err);
    });

    return () => unsubscribe();
  }, [user, currentMonthStr]);

  const trackReportDownload = async (reportType) => {
    if (!user || !user.email) return;
    const userEmailClean = (user.email || '').trim().toLowerCase();
    const reportId = `rep_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const record = {
      id: reportId,
      user_email: userEmailClean,
      user_id: user.uid || userEmailClean,
      userId: user.uid || userEmailClean,
      company_id: user.companyId || 'hitec',
      city_name: selectedProject?.city_name || cityName.trim() || '',
      report_type: reportType, // 'pdf', 'ppt', 'doc'
      download_date: todayStr,
      month_key: currentMonthStr,
      download_timestamp: Date.now(),
      created_at: new Date().toISOString()
    };

    // 1. Save to LocalStorage mock DB
    try {
      const cached = localStorage.getItem('hitecmedia_mock_db');
      const store = cached ? JSON.parse(cached) : {};
      if (!Array.isArray(store.report_downloads)) store.report_downloads = [];
      store.report_downloads.unshift(record);
      localStorage.setItem('hitecmedia_mock_db', JSON.stringify(store));

      const rawList = localStorage.getItem('hitec_report_downloads');
      const list = rawList ? JSON.parse(rawList) : [];
      list.unshift(record);
      localStorage.setItem('hitec_report_downloads', JSON.stringify(list));
    } catch (e) {}

    // 2. Save to Firestore
    try {
      await setDoc(doc(db, 'report_downloads', reportId), record, { merge: true });
    } catch (err) {
      console.warn("Firestore setDoc report_downloads error:", err);
    }

    // 3. Increment local counter immediately for optimistic feedback
    setMonthlyReportCount(prev => prev + 1);

    // 4. Dispatch event for open tabs & admin dashboard sync
    window.dispatchEvent(new CustomEvent('hitec_report_downloaded', { detail: record }));
    try {
      const bc = new BroadcastChannel('hitec_report_channel');
      bc.postMessage(record);
      bc.close();
    } catch (e) {}
  };

  // Sync photos in current project (auto-saved & persist permanently across logout/relogin)
  useEffect(() => {
    if (!selectedProject) {
      setProjectPhotos([]);
      return;
    }
    // Instant load cached photo list from selected project
    if (Array.isArray(selectedProject.photos) && selectedProject.photos.length > 0) {
      setProjectPhotos(selectedProject.photos);
    }
    const q = query(
      collection(db, 'photos'),
      where('project_id', '==', selectedProject.id),
      where('status', '==', 'done')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const photos = [];
      const nowMs = Date.now();
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.expires_at && new Date(data.expires_at).getTime() < nowMs) {
          return;
        }
        photos.push({ id: doc.id, ...data });
      });
      setProjectPhotos(prev => {
        // Merge DB photos with any local auto-saved photos, strictly deduplicating by filename
        const photoMap = new Map();
        prev.forEach(p => {
          const key = p.filename || p.id;
          if (key) photoMap.set(key, p);
        });
        photos.forEach(p => {
          const key = p.filename || p.id;
          if (key) {
            const existing = photoMap.get(key) || {};
            const merged = { ...existing, ...p };
            // Preserve local image previews if DB snapshot lacks them
            if (!merged.base64 && existing.base64) merged.base64 = existing.base64;
            if (!merged.url && existing.url) merged.url = existing.url;
            if (!merged.thumbnailUrl && existing.thumbnailUrl) merged.thumbnailUrl = existing.thumbnailUrl;
            if (!merged.annotatedBase64 && existing.annotatedBase64) merged.annotatedBase64 = existing.annotatedBase64;
            photoMap.set(key, merged);
          }
        });
        const resultList = Array.from(photoMap.values());
        // Fix 1: Preserve current editor index unless out of bounds
        setEditorIndex(prevIndex => (prevIndex !== null && prevIndex >= 0 && prevIndex < resultList.length) ? prevIndex : (resultList.length > 0 ? 0 : null));
        return resultList;
      });
      autosave({ snapshotsSyncedAt: Date.now() });

      // Automatically sync saved Firestore photos into projectQueues so photo list persists after relogging in
      setProjectQueues(prev => {
        const existingQueue = prev[selectedProject.id] || [];
        const activeItems = existingQueue.filter(item => item.status !== 'Done');
        const doneItemsFromDb = photos.map(photo => {
          const existingItem = existingQueue.find(q => q.finalFilename === photo.filename);
          const thumb = photo.base64 || photo.thumbnailUrl || photo.url || existingItem?.thumbnailUrl || '';
          return {
            id: photo.id,
            originalFilename: photo.filename,
            finalFilename: photo.filename,
            sizeKb: photo.size_kb || photo.sizeKb || existingItem?.sizeKb || 0,
            status: 'Done',
            progress: 100,
            thumbnailUrl: thumb,
            created_at: photo.created_at
          };
        });
        const combinedQueue = [...activeItems, ...doneItemsFromDb];
        const uniqueMap = new Map();
        combinedQueue.forEach(item => {
          if (item.finalFilename) {
            const existingInMap = uniqueMap.get(item.finalFilename);
            if (existingInMap && !item.thumbnailUrl && existingInMap.thumbnailUrl) {
              item.thumbnailUrl = existingInMap.thumbnailUrl;
            }
            uniqueMap.set(item.finalFilename, item);
          }
        });
        return {
          ...prev,
          [selectedProject.id]: Array.from(uniqueMap.values())
        };
      });
    });
    return () => unsubscribe();
  }, [selectedProject?.id]);

  // 20-Second Auto-Removal Engine for Warning/Error/Rejected/Blocked Photos across Desktop & Mobile
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const TIMEOUT_MS = 20000; // 20 seconds

      setProjectQueues(prevQueues => {
        let hasChanges = false;
        const nextQueues = {};

        Object.keys(prevQueues).forEach(projId => {
          const list = prevQueues[projId];
          if (!Array.isArray(list)) {
            nextQueues[projId] = list;
            return;
          }

          const filtered = list.filter(item => {
            const isWarningItem = item.status === 'Rejected' || item.status === 'Blocked' || item.status === 'Warning' || Boolean(item.error) || Boolean(item.warning);
            if (!isWarningItem) return true;

            const startedAt = item.warningStartedAt || item.startedAt || item.timestamp || now;
            const isExpired = (now - startedAt) >= TIMEOUT_MS;
            if (isExpired) {
              hasChanges = true;
              return false; // Auto-remove warning photo after 20 seconds
            }
            return true;
          });

          nextQueues[projId] = filtered;
        });

        return hasChanges ? nextQueues : prevQueues;
      });

      setProjectPhotos(prevPhotos => {
        const filtered = prevPhotos.filter(photo => {
          const isWarningPhoto = photo.status === 'Rejected' || photo.status === 'Blocked' || photo.status === 'Warning' || Boolean(photo.error) || Boolean(photo.warning);
          if (!isWarningPhoto) return true;

          const startedAt = photo.warningStartedAt || photo.timestamp || now;
          return (now - startedAt) < TIMEOUT_MS;
        });

        return filtered.length !== prevPhotos.length ? filtered : prevPhotos;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const lastActivePhotoKeyRef = useRef(null);

  // Update caption & assessor inputs ONLY when selected photo actually changes
  useEffect(() => {
    if (projectPhotos.length > 0 && editorIndex !== null && projectPhotos[editorIndex]) {
      const activePhoto = projectPhotos[editorIndex];
      const activePhotoKey = activePhoto.id || activePhoto.filename;

      if (lastActivePhotoKeyRef.current !== activePhotoKey) {
        lastActivePhotoKeyRef.current = activePhotoKey;
        const obs = activePhoto.comments_text || activePhoto.comments || '';
        const initialGrade = activePhoto.grade || activePhoto.assessment_grade || 'F2';
        const initialStatus = activePhoto.latest_status || activePhoto.status || 'Open';
        const initialLang = activePhoto.recommendations_lang || 'EN';
        const isManual = Boolean(activePhoto.manualOverride);
        setCommentsText(obs);
        setCommentsLang(activePhoto.comments_lang || 'EN');
        setRecommendations(Array.isArray(activePhoto.recommendations_json) ? activePhoto.recommendations_json : []);
        setRecommendationsLang(initialLang);
        setCaption(activePhoto.caption || '');
        setPhotoGrade(initialGrade);
        setPhotoStatus(initialStatus);
        setPhotoTitle(activePhoto.caption || activePhoto.title || activePhoto.asset_title || '');
        setPhotoDate(activePhoto.date || activePhoto.exif_date || new Date().toISOString().split('T')[0]);
        setPhotoLocation(activePhoto.location || selectedProject?.location || 'Site');
        setRecMode(isManual ? 'Manual' : 'Auto');
        const suggestions = getAISuggestions(obs, initialGrade, activePhoto.comments_lang || 'EN') || [];
        setAiSuggestions(Array.isArray(suggestions) ? suggestions : []);
        setAiSuggestedRecText(activePhoto.aiSuggestedRec || '');
      }
    } else if (projectPhotos.length === 0 || editorIndex === null) {
      lastActivePhotoKeyRef.current = null;
      setCommentsText('');
      setRecommendations([]);
      setCaption('');
      setPhotoGrade('F2');
      setPhotoStatus('Open');
      setPhotoTitle('');
      setPhotoDate(new Date().toISOString().split('T')[0]);
      setPhotoLocation(selectedProject?.location || 'Site');
      setRecMode('Auto');
      setAiSuggestions([]);
      setAiSuggestedRecText('');
    }
  }, [projectPhotos, editorIndex]);

  // Auto-generate AI Recommendation when in Auto mode upon comment or grade changes
  useEffect(() => {
    if (projectPhotos.length === 0 || !projectPhotos[editorIndex]) return;
    const currentLang = recommendationsLang || 'EN';
    const suggestions = getAISuggestions(commentsText, photoGrade, commentsLang || 'EN') || [];
    setAiSuggestions(Array.isArray(suggestions) ? suggestions : []);

    if (recMode === 'Auto') {
      let isMounted = true;
      generateRecommendation(commentsText, photoGrade, currentLang).then(recText => {
        if (isMounted && recText !== undefined && recText !== null) {
          const lines = (typeof recText === 'string' ? recText : '').split('\n').filter(Boolean);
          setRecommendations(lines);
          setAiSuggestedRecText(recText);
        }
      }).catch(err => {
        console.error("generateRecommendation auto error:", err);
      });
      return () => { isMounted = false; };
    }
  }, [commentsText, photoGrade, recMode, recommendationsLang, commentsLang, editorIndex]);

  // Requirement 1: Debounced Auto-Save Logic (2-second debounce) whenever photo list or captions change
  useEffect(() => {
    if (!selectedProject || !user || !projectPhotos) return;

    const timer = setTimeout(async () => {
      const nowIso = new Date().toISOString();
      const cleanPhotos = projectPhotos.map(p => ({
        id: p.id || ("photo_" + Math.random().toString(36).substring(7)),
        filename: p.filename || p.originalFilename || '',
        url: p.url || '',
        base64: p.base64 || p.thumbnailUrl || '',
        thumbnailUrl: p.thumbnailUrl || p.base64 || p.url || '',
        annotatedBase64: p.annotatedBase64 || '',
        caption: p.caption || '',
        title: p.title || p.caption || '',
        asset_title: p.asset_title || p.caption || '',
        date: p.date || p.exif_date || '',
        location: p.location || p.exif_gps || '',
        grade: p.grade || p.assessment_grade || 'F2',
        assessment_grade: p.assessment_grade || p.grade || 'F2',
        status: p.status || p.latest_status || 'Open',
        latest_status: p.latest_status || p.status || 'Open',
        comments_text: p.comments_text || p.comments || '',
        comments: p.comments || p.comments_text || '',
        comments_lang: p.comments_lang || 'EN',
        recommendations_json: Array.isArray(p.recommendations_json) ? p.recommendations_json : [],
        recommendations_lang: p.recommendations_lang || 'EN',
        exif_date: p.exif_date || p.date || '',
        exif_gps: p.exif_gps || p.location || '',
        size_kb: p.size_kb || p.sizeKb || 0,
        created_at: p.created_at || nowIso
      }));

      // 1. Save data to Firestore (projects/{projectId}) immediately with merge: true
      try {
        const expiresIso = new Date(Date.now() + retentionMs).toISOString();
        await setDoc(doc(db, 'projects', selectedProject.id), {
          userId: user.uid || '',
          created_by: (user.email || '').trim().toLowerCase(),
          company_id: user.companyId || 'hitec',
          retention_days: retentionDays,
          expires_at: expiresIso,
          photos: cleanPhotos,
          lastModified: nowIso
        }, { merge: true });
      } catch (err) {
        console.error("Auto-save error updating project in Firestore:", err);
      }

      // 2. Save data to Browser LocalStorage cache
      setProjects(prev => {
        const updated = prev.map(proj =>
          proj.id === selectedProject.id
            ? { ...proj, photos: cleanPhotos, lastModified: nowIso }
            : proj
        );
        saveProjectsToCache(user, updated);
        return updated;
      });
      autosave({ photos: cleanPhotos });
    }, 1500);

    return () => clearTimeout(timer);
  }, [projectPhotos, selectedProject?.id, user]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    if (!companyName.trim() || !cityName.trim()) {
      setFieldValidationErrors({
        company: !companyName.trim(),
        city: !cityName.trim()
      });
      alert("Please enter both Company and City names before creating a project.");
      return;
    }
    setFieldValidationErrors({ company: false, city: false });
    try {
      const nowIso = new Date().toISOString();
      const expiresIso = new Date(Date.now() + retentionMs).toISOString();
      const projectId = "proj_" + Math.random().toString(36).substring(2, 11);
      const newProjObj = {
        id: projectId,
        name: newProjectName.trim(),
        created_at: nowIso,
        lastModified: nowIso,
        created_by: (user.email || '').trim().toLowerCase(),
        userId: user.uid || '',
        company_id: user.companyId || 'hitec',
        company_name: companyName.trim(),
        city_name: cityName.trim(),
        retention_days: retentionDays,
        expires_at: expiresIso,
        photos: []
      };
      try {
        await setDoc(doc(db, 'projects', projectId), newProjObj, { merge: true });
      } catch (fsErr) {
        console.warn("Mock/offline project creation note:", fsErr);
      }
      setNewProjectName('');
      setSelectedProject(newProjObj);
      setIsHeaderCollapsed(true); // RULE 2: Auto force collapse when project IS created
      setProjects(prev => {
        if (prev.some(p => p.id === newProjObj.id)) {
          const updated = prev.map(p => p.id === newProjObj.id ? newProjObj : p);
          saveProjectsToCache(user, updated);
          return updated;
        }
        const updated = [...prev, newProjObj];
        saveProjectsToCache(user, updated);
        return updated;
      });
    } catch (err) {
      console.error("Error creating project:", err);
    }
  };

  const handleRenameProject = async () => {
    if (!selectedProject) return;
    const newName = prompt("Rename project to:", selectedProject.name);
    if (!newName || !newName.trim()) return;
    try {
      await updateDoc(doc(db, 'projects', selectedProject.id), {
        name: newName.trim()
      });
      setSelectedProject(prev => prev ? { ...prev, name: newName.trim() } : null);
      autosave({ name: newName.trim() });
    } catch (err) {
      console.error("Error renaming project:", err);
    }
  };

  const handleDeleteProject = async () => {
    if (!selectedProject) return;
    const confirmed = confirm(`Are you sure you want to delete the project "${selectedProject.name}" and all its photos?`);
    if (!confirmed) return;
    try {
      await deleteDoc(doc(db, 'projects', selectedProject.id));
      const photosSnap = await getDocs(query(collection(db, 'photos'), where('project_id', '==', selectedProject.id)));
      for (const photoDoc of photosSnap.docs) {
        await deleteDoc(doc(db, 'photos', photoDoc.id));
      }
      setSelectedProject(null);
    } catch (err) {
      console.error("Error deleting project:", err);
    }
  };

  const handleFilesSelected = async (files) => {
    if (!selectedProject) {
      alert("Please select or create a project first.");
      return;
    }

    const currentProjectPhotoCount = projectPhotos.length + queue.length;
    let availableProjectSlots = 200 - currentProjectPhotoCount;
    if (availableProjectSlots <= 0) {
      alert("Project photo limit reached! A single project can hold up to 200 photos maximum.");
      return;
    }

    // Intercept and dynamically compress high-resolution photos (2MB-8MB) down below 300KB
    const targetKb = Math.min((planLimits.maxKb || 300) - 5, 190);
    const needsCompression = files.some(file => file && file.type && file.type.startsWith('image/') && (file.size / 1024) > targetKb);

    if (needsCompression) {
      setIsCompressing(true);
    }

    let processedFiles = files;
    try {
      processedFiles = await Promise.all(
        files.map(async (file) => {
          if (file && file.type && file.type.startsWith('image/') && (file.size / 1024) > targetKb) {
            try {
              return await compressImage(file, targetKb);
            } catch (err) {
              console.warn("Client image compression error, falling back to original file:", err);
              return file;
            }
          }
          return file;
        })
      );
    } finally {
      if (needsCompression) {
        setIsCompressing(false);
      }
    }

    let currentRemaining = planLimits.maxDaily - dailyUploadCount;
    const newQueueItems = [];
    const oversizedFiles = [];
    let limitReachedHit = false;
    let projectLimitAlertHit = false;

    processedFiles.forEach((file) => {
      if (availableProjectSlots <= 0) {
        if (!projectLimitAlertHit) {
          alert("Project photo limit reached! Only up to 200 photos can be appended per project.");
          projectLimitAlertHit = true;
        }
        return;
      }
      availableProjectSlots--;

      const sizeKb = file.size / 1024;
      const extension = file.name.split('.').pop().toLowerCase();
      const tempId = Math.random().toString(36).substring(7);

      // Instantly create a local object URL for immediate thumbnail rendering
      const thumbnailUrl = URL.createObjectURL(file);

      let status = 'Queued';
      let error = null;

      // Client size check (< 300 KB)
      if (sizeKb > planLimits.maxKb) {
        oversizedFiles.push({ name: file.name, sizeKb: Math.round(sizeKb) });
        return;
      }

      // Find highest existing sequence number for today across queue, projectPhotos, and newly added items
      let maxSeq = 0;
      const existingNames = [
        ...queue.map(q => q.finalFilename),
        ...projectPhotos.map(p => p.filename),
        ...newQueueItems.map(i => i.finalFilename)
      ].filter(Boolean);

      existingNames.forEach(name => {
        const match = name.match(/-(\d{3,})\./);
        if (match && match[1]) {
          const num = parseInt(match[1], 10);
          if (!isNaN(num) && num > maxSeq) maxSeq = num;
        }
      });

      const nextSeq = Math.max(
        maxSeq + 1,
        dailyUploadCount + newQueueItems.filter(i => i.status === 'Queued').length + 1
      );
      const activeProjectName = selectedProject?.name
        ? selectedProject.name.toLowerCase().replace(/\s+/g, '_')
        : (currentProject?.name ? currentProject.name.toLowerCase().replace(/\s+/g, '_') : 'demo');
      const seqStr = String(nextSeq).padStart(3, '0');
      let finalFilename = `${activeProjectName}_${todayStr}-${seqStr}.${extension}`;

      let collisionCounter = 1;
      while (existingNames.includes(finalFilename)) {
        const guardedSeq = String(nextSeq + collisionCounter).padStart(3, '0');
        finalFilename = `${activeProjectName}_${todayStr}-${guardedSeq}.${extension}`;
        collisionCounter++;
      }

      newQueueItems.push({
        id: tempId,
        file,
        originalName: file.name,
        finalFilename,
        sizeKb,
        status: 'Queued',
        error: null,
        progress: 0,
        retries: 0,
        thumbnailUrl   // instant local preview URL
      });
    });

    // Show alert popup if file exceeded 300 KB
    if (oversizedFiles.length > 0) {
      setAlertPopup({
        title: "File Size Limit Exceeded",
        message: `${oversizedFiles.length === 1 ? `"${oversizedFiles[0].name}" (${oversizedFiles[0].sizeKb} KB)` : `${oversizedFiles.length} photos`} exceeds your plan limit of ${planLimits.maxKb} KB. Maximum allowed size per photo is ${planLimits.maxKb} KB (300 KB).`,
        type: 'error'
      });
    }

    // Immediately commit all queue items to state — thumbnails render at once
    setQueue(prev => [...prev, ...newQueueItems]);
    if (newQueueItems.length > 0) {
      autosave({ newSnapshotsAddedAt: Date.now() }, { immediate: true });
    }

    // Pipe all eligible items through the parallel worker pool (6 concurrent slots)
    newQueueItems.forEach(item => {
      if (item.status === 'Queued') {
        workerPoolRef.current.add(() => uploadFile(item));
      }
    });
  };

  const uploadFile = async (item) => {
    const filePath = `projects/${selectedProject.id}/${todayStr}/${item.finalFilename}`;
    const storageRef = ref(storage, filePath);

    setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Uploading', startedAt: Date.now() } : q));

    // In production: request a GCS Signed URL and PUT directly to GCS
    // In mock mode: signed_url will be empty, we fall back to uploadBytesResumable
    let usedDirectPut = false;
    if (!isMockMode) {
      try {
        const getUrlFn = httpsCallable(functions, 'getSignedUploadUrl');
        const { data: { signed_url } } = await getUrlFn({
          gcs_path: filePath,
          content_type: item.file.type || 'image/jpeg'
        });

        if (signed_url) {
          usedDirectPut = true;
          // Direct browser → GCS PUT with XHR for progress events + exponential backoff
          await withExponentialBackoff(
            () => new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open('PUT', signed_url);
              xhr.setRequestHeader('Content-Type', item.file.type || 'image/jpeg');
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const pct = Math.round((e.loaded / e.total) * 100);
                  setQueue(prev => prev.map(q => q.id === item.id ? { ...q, progress: pct, reached100At: (pct === 100 ? (q.reached100At || Date.now()) : q.reached100At) } : q));
                }
              };
              xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`PUT ${xhr.status}`));
              xhr.onerror = () => reject(new Error('Network error during PUT'));
              xhr.send(item.file);
            }),
            3,
            (attempt, delay) => {
              setQueue(prev => prev.map(q =>
                q.id === item.id ? { ...q, status: 'Retrying', retries: attempt } : q
              ));
            }
          );
        }
      } catch (err) {
        console.warn('Signed URL path failed, falling back to SDK upload:', err);
      }
    }

    // Fallback / Mock Mode: use Firebase SDK uploadBytesResumable
    if (!usedDirectPut) {
      await withExponentialBackoff(
        () => new Promise((resolve, reject) => {
          const uploadTask = uploadBytesResumable(storageRef, item.file);
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
              setQueue(prev => prev.map(q => q.id === item.id ? { ...q, progress: pct, reached100At: (pct === 100 ? (q.reached100At || Date.now()) : q.reached100At) } : q));
            },
            (error) => reject(error),
            () => resolve(uploadTask.snapshot.ref)
          );
        }),
        3,
        (attempt, delay) => {
          setQueue(prev => prev.map(q =>
            q.id === item.id ? { ...q, status: 'Retrying', retries: attempt } : q
          ));
        }
      ).then(async (snapshotRef) => {
        // ── MOCK MODE: no real backend fires onSnapshot, mark Done immediately ──
        if (isMockMode) {
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Done', progress: 100 } : q));
          const photoId = `${selectedProject?.id || 'demo'}_${item.finalFilename.replace(/\./g, '_')}`;
          const newPhotoObj = {
            id: photoId,
            project_id: selectedProject?.id || 'demo',
            company_id: user?.companyId || 'hitec',
            city_name: selectedProject?.city_name || cityName?.trim() || '',
            filename: item.finalFilename,
            original_filename: item.originalName,
            url: item.thumbnailUrl || '',
            base64: item.thumbnailUrl || '',
            size_kb: item.sizeKb,
            upload_date: todayStr,
            upload_timestamp: Date.now(),
            uploaded_by: user?.email || '',
            caption: '',
            comments_text: '',
            comments: '',
            comments_lang: 'EN',
            recommendations_json: [],
            recommendations_lang: 'EN',
            status: 'done',
            created_at: new Date().toISOString()
          };
          try {
            await setDoc(doc(db, 'photos', photoId), newPhotoObj, { merge: true });
          } catch (e) {
            console.warn('Mock setDoc error:', e);
          }
          setProjectPhotos(prev => {
            if (prev.some(p => p.filename === item.finalFilename)) return prev;
            const updated = [...prev, newPhotoObj];
            if (selectedProject) {
              setSelectedProject(prevProj => prevProj ? { ...prevProj, photos: updated } : null);
            }
            return updated;
          });
          setEditorIndex(prev => prev === null ? 0 : prev);
          autosave({ snapshotCompletedAt: Date.now() }, { immediate: true });
          return;
        }

        const downloadUrl = await getDownloadURL(snapshotRef || storageRef);

        // Pre-create Firestore photo doc
        const photoId = `${selectedProject.id}_${item.finalFilename.replace(/\./g, '_')}`;
        const photoDocRef = doc(db, 'photos', photoId);

        await setDoc(photoDocRef, {
          id: photoId,
          project_id: selectedProject.id,
          company_id: user.companyId,
          city_name: selectedProject?.city_name || cityName.trim() || localStorage.getItem('hitec_city_name') || '',
          filename: item.finalFilename,
          original_filename: item.originalName,
          gcs_path: filePath,
          url: downloadUrl,
          size_kb: item.sizeKb,
          upload_date: todayStr,
          upload_timestamp: Date.now(),
          uploaded_by: user.email,
          caption: '',
          status: 'pending',
          created_at: new Date().toISOString()
        });

        // Listen for backend validation result, with 30s stuck-watchdog
        const unsub = onSnapshot(photoDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.status === 'done') {
              setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Done', progress: 100 } : q));
              autosave({ snapshotCompletedAt: Date.now() }, { immediate: true });
              if (unsub) unsub();
              clearTimeout(stuckTimer);
            } else if (data.status === 'rejected') {
              setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Rejected', error: data.reason || 'Rejected by Server', warningStartedAt: Date.now() } : q));
              if (unsub) unsub();
              clearTimeout(stuckTimer);
            }
          }
        });

        // Watchdog: if still Uploading after 30s, auto-remove the stuck item
        const stuckTimer = setTimeout(() => {
          if (unsub) unsub();
          setQueue(prev => {
            const stuckItem = prev.find(q => q.id === item.id);
            if (stuckItem && stuckItem.status === 'Uploading') {
              console.warn('[Watchdog] Removing stuck upload item:', item.finalFilename);
              return prev.filter(q => q.id !== item.id);
            }
            return prev;
          });
        }, 30000);
      }).catch((err) => {
        console.error('Upload failed after retries:', err);
        setQueue(prev => prev.map(q =>
          q.id === item.id ? { ...q, status: 'Rejected', error: 'Upload failed after 3 retries', warningStartedAt: Date.now() } : q
        ));
      });
    }
  };

  // Production direct-PUT completion handler: called when GCS fires onPhotoUpload
  // which auto-updates Firestore; we just need to register the doc and listen.
  const registerAndListenAfterDirectPut = async (item, filePath) => {
    const photoId = `${selectedProject.id}_${item.finalFilename.replace(/\./g, '_')}`;
    const photoDocRef = doc(db, 'photos', photoId);

    await setDoc(photoDocRef, {
      id: photoId,
      project_id: selectedProject.id,
      company_id: user.companyId,
      city_name: selectedProject?.city_name || cityName.trim() || localStorage.getItem('hitec_city_name') || '',
      filename: item.finalFilename,
      original_filename: item.originalName,
      gcs_path: filePath,
      url: '',
      size_kb: item.sizeKb,
      upload_date: todayStr,
      upload_timestamp: Date.now(),
      uploaded_by: user.email,
      caption: '',
      status: 'pending',
      created_at: new Date().toISOString()
    });

    const unsub = onSnapshot(photoDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.status === 'done') {
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Done', progress: 100 } : q));
          autosave({ snapshotCompletedAt: Date.now() }, { immediate: true });
          if (unsub) unsub();
          clearTimeout(stuckTimer);
        } else if (data.status === 'rejected') {
          setQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'Rejected', error: data.reason || 'Rejected by Server', warningStartedAt: Date.now() } : q));
          if (unsub) unsub();
          clearTimeout(stuckTimer);
        }
      }
    });

    // Watchdog: if still Uploading after 30s, auto-remove the stuck item
    const stuckTimer = setTimeout(() => {
      if (unsub) unsub();
      setQueue(prev => {
        const stuckItem = prev.find(q => q.id === item.id);
        if (stuckItem && stuckItem.status === 'Uploading') {
          console.warn('[Watchdog] Removing stuck upload item:', item.finalFilename);
          return prev.filter(q => q.id !== item.id);
        }
        return prev;
      });
    }, 30000);
  };

  const handleRefreshPhotoOrder = () => {
    if (!selectedProject) return;
    const orderedQueue = queue.filter(item => item.status === 'Done');
    let reorderedPhotos = [...projectPhotos];
    setProjectPhotos(prev => {
      const prevMap = new Map();
      prev.forEach(p => {
        const key = p.filename || p.id;
        if (key) prevMap.set(key, p);
      });

      const updatedPhotos = orderedQueue.map(q => {
        const existing = prevMap.get(q.finalFilename) || {};
        return {
          ...existing,
          id: existing.id || q.id || ("photo_" + Math.random().toString(36).substring(7)),
          filename: q.finalFilename || existing.filename,
          url: existing.url || q.previewUrl || q.thumbnailUrl || '',
          base64: existing.base64 || q.thumbnailUrl || '',
          caption: existing.caption || '',
          comments_text: existing.comments_text || existing.comments || '',
          comments: existing.comments || existing.comments_text || '',
          comments_lang: existing.comments_lang || 'EN',
          recommendations_json: existing.recommendations_json || [],
          recommendations_lang: existing.recommendations_lang || 'EN',
          size_kb: existing.size_kb || q.sizeKb || 0,
          created_at: existing.created_at || new Date().toISOString()
        };
      });
      reorderedPhotos = updatedPhotos;
      if (selectedProject) {
        setSelectedProject(prevProj => prevProj ? { ...prevProj, photos: updatedPhotos } : null);
      }
      return updatedPhotos;
    });
    autosave({ photos: reorderedPhotos, orderRefreshedAt: Date.now() }, { immediate: true });
    setEditorIndex(0);
  };

  const handleAiObservationAssessor = async (targetLang = commentsLang) => {
    setAiGrammarChecking(true);
    try {
      const photoObj = projectPhotos[editorIndex] || {};
      const res = await aiObservationAssessor(photoObj, commentsText, targetLang);
      if (res && res.observations) {
        const obsText = res.observations.join('\n');
        setCommentsText(obsText);
        updatePhotoFieldAndAutosave({ comments_text: obsText, comments: obsText });

        if (photoObj) {
          const recRes = await aiGenerateRecommendation(
            photoObj,
            obsText,
            targetLang,
            'Teknis (ATEX NFPA oriented)'
          );
          if (recRes && recRes.recommendations) {
            setRecommendations(recRes.recommendations);
            updatePhotoFieldAndAutosave({ recommendations_json: recRes.recommendations, recommendations: recRes.recommendations });
          }
        }
      }
    } catch (err) {
      console.error("AI Observation Assessor error:", err);
    } finally {
      setAiGrammarChecking(false);
    }
  };

  const handleAiGrammarCheck = handleAiObservationAssessor;

  const handleLanguageSwitch = async (targetLang) => {
    setCommentsLang(targetLang);
    setRecommendationsLang(targetLang);

    setAiGrammarChecking(true);
    try {
      const photoObj = projectPhotos[editorIndex] || {};
      let updatedComments = commentsText;

      // 1. Translate and grammar-check Observation text if present, otherwise generate observation
      if (commentsText && commentsText.trim()) {
        const translatedObs = await aiTranslateAndGrammarCheck(commentsText, targetLang, 'observation');
        if (translatedObs) {
          updatedComments = translatedObs;
          setCommentsText(updatedComments);
          updatePhotoFieldAndAutosave({ comments_text: updatedComments, comments: updatedComments, comments_lang: targetLang });
        }
      } else {
        const res = await aiObservationAssessor(photoObj, commentsText, targetLang);
        if (res && res.observations) {
          updatedComments = res.observations.join('\n');
          setCommentsText(updatedComments);
          updatePhotoFieldAndAutosave({ comments_text: updatedComments, comments: updatedComments, comments_lang: targetLang });
        }
      }

      // 2. Translate and grammar-check Recommendation text if present, otherwise generate recommendations contextually
      if (recommendations && recommendations.length > 0) {
        const recText = recommendations.join('\n');
        const translatedRecsText = await aiTranslateAndGrammarCheck(recText, targetLang, 'recommendation');
        if (translatedRecsText) {
          const translatedRecsArr = translatedRecsText.split('\n').map(r => r.trim()).filter(Boolean);
          setRecommendations(translatedRecsArr);
          updatePhotoFieldAndAutosave({ recommendations_json: translatedRecsArr, recommendations: translatedRecsArr, recommendations_lang: targetLang });
        }
      } else if (photoObj) {
        const recRes = await aiGenerateRecommendation(
          photoObj,
          updatedComments,
          targetLang
        );
        if (recRes && recRes.recommendations) {
          setRecommendations(recRes.recommendations);
          updatePhotoFieldAndAutosave({ recommendations_json: recRes.recommendations, recommendations: recRes.recommendations, recommendations_lang: targetLang });
        }
      }
    } catch (err) {
      console.error("Language switch translation error:", err);
    } finally {
      setAiGrammarChecking(false);
    }
  };

  const handleGenerateRecommendation = async () => {
    if (!projectPhotos[editorIndex]) return;
    setAiGeneratingRec(true);
    try {
      const recText = await generateRecommendation(commentsText, photoGrade, recommendationsLang);
      if (recText) {
        const lines = recText.split('\n').filter(Boolean);
        setRecommendations(lines);
        setAiSuggestedRecText(recText);
        setRecMode('Auto');
        updatePhotoFieldAndAutosave({ recommendations_json: lines, recommendations: lines, aiSuggestedRec: recText });
      } else {
        const res = await aiGenerateRecommendation(
          projectPhotos[editorIndex],
          commentsText,
          recommendationsLang
        );
        if (res && res.recommendations) {
          setRecommendations(res.recommendations);
          setAiSuggestedRecText(res.recommendations.join('\n'));
          setRecMode('Auto');
          updatePhotoFieldAndAutosave({ recommendations_json: res.recommendations, recommendations: res.recommendations, aiSuggestedRec: res.recommendations.join('\n') });
        }
      }
    } catch (err) {
      console.error("AI Recommendation error:", err);
    } finally {
      setAiGeneratingRec(false);
    }
  };

  const handleSaveAnnotatedImage = async (photo, dataURL, annotationsObj) => {
    if (!photo) return;
    const updatePayload = {
      annotatedBase64: dataURL,
      base64: dataURL,
      thumbnailUrl: dataURL,
      previewUrl: dataURL,
      url: dataURL,
      annotations: annotationsObj
    };

    setProjectPhotos(prev => prev.map(p => 
      (p.id && p.id === photo.id) || p.filename === photo.filename ? { ...p, ...updatePayload } : p
    ));

    setQueue(prev => prev.map(q => 
      (q.id && q.id === photo.id) || q.finalFilename === photo.filename || q.filename === photo.filename ? { ...q, ...updatePayload } : q
    ));

    if (selectedProject) {
      const updatedPhotos = (selectedProject.photos || []).map(p =>
        (p.id && p.id === photo.id) || p.filename === photo.filename ? { ...p, ...updatePayload } : p
      );
      setSelectedProject(prev => ({ ...prev, photos: updatedPhotos }));
      updateDoc(doc(db, 'projects', selectedProject.id), { photos: updatedPhotos }).catch(() => {});
      autosave({ photos: updatedPhotos });
    }

    if (photo.id) {
      try {
        await updateDoc(doc(db, 'photos', photo.id), updatePayload);
      } catch (e) {
        console.error("Error saving annotations to Firestore:", e);
      }
    }
  };

  const getExportFileName = (extension) => {
    const company = companyName.trim() ? companyName.trim().replace(/[\\/:*?"<>|]/g, "_") : (selectedProject?.name || "Company").replace(/[\\/:*?"<>|]/g, "_");
    const city = cityName.trim() ? cityName.trim().replace(/[\\/:*?"<>|]/g, "_") : "City";
    const year = new Date().getFullYear();
    const ext = extension.replace(/^\./, '');
    return `${company}_${city}_${year}.${ext}`;
  };

  const handleExport = async (format) => {
    if (!selectedProject) return;
    setExportError(null);
    if (format === 'pptx') {
      setExportingPPTX(true);
    } else {
      setExportingXLSX(true);
    }

    try {
      // Call python functions (HTTPS callables mapped to PDF output)
      const functionName = format === 'pptx' ? 'exportPPTX' : 'exportPDF';
      const exportFn = httpsCallable(functions, functionName);

      // Order exported photos strictly based on their row number / position in the queue list
      const orderedQueue = queue.filter(item => 
        item.status === 'Done' && (selectedPhotos.length === 0 || selectedPhotos.includes(item.finalFilename))
      );

      const queueFilenames = new Set(orderedQueue.map(q => q.finalFilename));
      const fallbackPhotos = projectPhotos.filter(p => 
        !queueFilenames.has(p.filename) && (selectedPhotos.length === 0 || selectedPhotos.includes(p.filename))
      );

      const photosWithUrls = [
        ...orderedQueue.map(item => {
          const matchedPhoto = projectPhotos.find(p => p.filename === item.finalFilename) || {};
          return {
            ...matchedPhoto,
            ...item,
            filename: item.finalFilename,
            localUrl: item.thumbnailUrl || matchedPhoto.url || matchedPhoto.thumbnailUrl || '',
            caption: matchedPhoto.caption || item.caption || ''
          };
        }),
        ...fallbackPhotos.map(p => ({
          ...p,
          localUrl: queue.find(q => q.finalFilename === p.filename)?.thumbnailUrl || p.url || p.thumbnailUrl || ''
        }))
      ];
      
      const photosWithBase64 = await Promise.all(photosWithUrls.map(async (p) => {
        const bestImageBase64 = await getBestPhotoBase64(p);
        return {
          ...p,
          base64: bestImageBase64,
          annotatedBase64: bestImageBase64,
          caption: p.caption || p.title || p.asset_title || "",
          comments: p.comments || p.comments_text || "",
          comments_text: p.comments_text || p.comments || "",
          recommendation: isMobileMode ? "" : (p.recommendation || ""),
          recommendations: isMobileMode ? [] : (p.recommendations || [])
        };
      }));

      const response = await exportFn({ 
        project_id: selectedProject.id,
        project_name: selectedProject.name || 'Project',
        company_name: companyName,
        city_name: cityName,
        export_filename: getExportFileName(format === 'pptx' ? 'pptx' : 'pdf'),
        selected_photos: selectedPhotos,
        view_mode: isMobileMode ? 'Mobile' : 'Desktop',
        photos_data: photosWithBase64,
        photos: photosWithBase64
      });
      const { downloadUrl, valid } = response.data || {};
      
      // Track successful report download for daily usage and admin analytics
      trackReportDownload(format === 'pptx' ? 'ppt' : 'pdf');

      if (downloadUrl) {
        try {
          const res = await fetch(downloadUrl);
          const blob = await res.blob();
          const filename = getExportFileName(format === 'pptx' ? 'pptx' : format === 'excel' ? 'xlsx' : 'pdf');
          let mime = 'application/pdf';
          if (format === 'pptx') mime = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          if (format === 'excel') mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          await shareFile(blob, filename, mime);
        } catch (fetchErr) {
          console.error("Error fetching blob for shareFile, falling back to direct link:", fetchErr);
          window.open(downloadUrl, '_blank');
        }
      } else if (!valid && !isMockMode && downloadUrl !== "") {
        throw new Error("No download URL returned.");
      }
    } catch (err) {
      console.error(`Export ${format} error:`, err);
      setExportError(`Export failed: ${err.message || 'Server error'}`);
    } finally {
      if (format === 'pptx') {
        setExportingPPTX(false);
      } else {
        setExportingXLSX(false);
      }
    }
  };

  const onExportWordClick = async () => {
    if (!selectedProject) return;
    setExportError(null);
    setExportingDOCX(true);
    try {
      const filename = getExportFileName('docx');
      const projectPayload = {
        ...selectedProject,
        photos: projectPhotos.length > 0 ? projectPhotos : (selectedProject?.photos || []),
        company_name: companyName,
        city_name: cityName
      };
      const result = await handleExportWord(projectPayload, queue, selectedPhotos, filename, isMobileMode ? 'Mobile' : 'Desktop', true);
      
      // Track successful Word/DOC report download for daily usage and admin analytics
      trackReportDownload('doc');

      if (result && result.blob) {
        const mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        await shareFile(result.blob, result.filename || filename, mime);
      }
    } catch (err) {
      console.error("Error exporting Word report:", err);
      setExportError(`Word export failed: ${err.message || 'Error'}`);
    } finally {
      setExportingDOCX(false);
    }
  };

  const handlePublishBarExport = async (key) => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectivityModal({
        show: true,
        action: () => handlePublishBarExport(key)
      });
      return;
    }

    if (key === 'confirm' || key === 'save') {
      if (!selectedProject) return;
      const nowIso = new Date().toISOString();
      const expiresIso = new Date(Date.now() + retentionMs).toISOString();
      
      // Auto select all queued done photos if none currently selected
      if (selectedPhotos.length === 0 && projectPhotos.length > 0) {
        const allDoneNames = projectPhotos.map(p => p.filename).filter(Boolean);
        setSelectedPhotos(allDoneNames);
      }

      // Execute immediate manual auto-save of project data & photo list
      autosave({
        photos: projectPhotos,
        manualSaveAt: Date.now(),
        lastEditedAt: nowIso
      }, { immediate: true });

      // Save to cloud & local store cache
      try {
        await setDoc(doc(db, 'projects', selectedProject.id), {
          status: 'active',
          photos: projectPhotos,
          retention_days: retentionDays,
          expires_at: expiresIso,
          updated_at: nowIso,
          last_saved_by: user.email || '',
          company_id: user.companyId || 'hitec'
        }, { merge: true });
      } catch (err) {
        console.error("Error saving project to cloud:", err);
      }

      for (const p of projectPhotos) {
        if (p.id) {
          try {
            await updateDoc(doc(db, 'photos', p.id), {
              status: 'done',
              expires_at: expiresIso,
              retention_days: retentionDays,
              company_id: user.companyId || 'hitec',
              city_name: selectedProject?.city_name || cityName.trim() || localStorage.getItem('hitec_city_name') || '',
              upload_date: p.upload_date || todayStr,
              upload_timestamp: p.upload_timestamp || Date.now()
            });
          } catch (e) {}
        }
      }

      setConfirmedExports(true);
    } else if (key === 'ppt') {
      await handleExport('pptx');
    } else if (key === 'word' || key === 'doc') {
      await onExportWordClick();
    } else if (key === 'excel') {
      await handleExport('excel');
    } else if (key === 'pdf') {
      await handleExport('pdf');
    }
  };

  const handleWhatsAppShare = async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectivityModal({ show: true, action: handleWhatsAppShare });
      return;
    }
    if (mobileShareModal?.exportFn) mobileShareModal.exportFn();
    const shareTitle = `${selectedProject?.name || 'Project'} Report (${mobileShareModal?.type})`;
    const shareText = `Here is the ${mobileShareModal?.type} inspection report for ${selectedProject?.name || 'Project'}.`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText });
        setMobileShareModal(null);
        return;
      } catch (e) {}
    }
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} File: ${mobileShareModal?.fileName}`)}`, '_blank');
    setMobileShareModal(null);
  };

  const handleGoogleDriveShare = async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectivityModal({ show: true, action: handleGoogleDriveShare });
      return;
    }
    if (mobileShareModal?.exportFn) mobileShareModal.exportFn();
    const shareTitle = `${selectedProject?.name || 'Project'} Report (${mobileShareModal?.type})`;
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: `Upload to Google Drive: ${mobileShareModal?.fileName}` });
        setMobileShareModal(null);
        return;
      } catch (e) {}
    }
    window.open(`https://drive.google.com/drive/my-drive`, '_blank');
    setMobileShareModal(null);
  };

  const handleRemove = async () => {

    let photosToDelete = [];
    if (allDoneSelected) {
      photosToDelete = [...projectPhotos];
    } else if (selectedPhotos.length > 0) {
      photosToDelete = projectPhotos.filter(p => 
        selectedPhotos.includes(p.filename) || selectedPhotos.includes(p.id)
      );
    }

    if (photosToDelete.length === 0) {
      if (allDoneSelected) {
        setQueue([]);
        setSelectedPhotos([]);
      } else if (selectedPhotos.length > 0) {
        setQueue(prev => prev.filter(item => !selectedPhotos.includes(item.finalFilename)));
        setSelectedPhotos([]);
      }
      return;
    }

    // 1. Delete docs from photos collection
    for (const photo of photosToDelete) {
      if (photo.id) {
        try {
          await deleteDoc(doc(db, 'photos', photo.id));
        } catch (e) {
          console.error("Error deleting photo doc:", e);
        }
      }
    }

    // 2. Update local state
    const deletedFilenames = new Set(photosToDelete.map(p => p.filename));
    const deletedIds = new Set(photosToDelete.map(p => p.id));

    const remainingPhotos = projectPhotos.filter(p => !deletedFilenames.has(p.filename) && !deletedIds.has(p.id));
    setProjectPhotos(remainingPhotos);
    setQueue(prev => prev.filter(item => !deletedFilenames.has(item.finalFilename)));
    setSelectedPhotos([]);

    // 3. Update project record so cached photos list stays permanently in sync
    if (selectedProject) {
      const updatedProject = {
        ...selectedProject,
        photos: remainingPhotos
      };
      setSelectedProject(updatedProject);
      try {
        await updateDoc(doc(db, 'projects', selectedProject.id), { photos: remainingPhotos });
      } catch (e) {}
      autosave({ photos: remainingPhotos }, { immediate: true });
    }
  };

  const isPro = planLimits.maxDaily === 300;

  return (
    <div className="flex h-screen h-[100dvh] max-h-[100dvh] flex-col bg-slate-950 text-slate-100 overflow-hidden">
      {/* Navbar using standalone Header and AuthProvider */}
      <AuthProvider value={{
        user: {
          ...user,
          role: user?.role || 'user',
          plan: {
            dailyPhotoLimit: user?.plan?.dailyPhotoLimit || planLimits.maxDaily || (user?.role === 'user' ? 100 : 9999)
          }
        },
        usage: {
          reportsUsedMonthly: monthlyReportCount,
          reportsUsedToday: monthlyReportCount,
          photosUsedToday: dailyUploadCount
        },
        onLogout,
        onOpenFeedback: () => setShowFeedbackModal(true),
        onOpenSecurity
      }}>
        <Header />
      </AuthProvider>

      {/* Main Grid Container with Swipe Track */}
      <div 
        className="flex flex-1 overflow-hidden relative w-full"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={`${isMobileMode ? 'w-full' : 'w-[200vw] md:w-full'} h-full flex transition-transform duration-300 ease-in-out md:translate-x-0 md:transition-none ${
          !isMobileMode && activeTab !== 'upload' && activeTab !== 'export' ? '-translate-x-[100vw]' : 'translate-x-0'
        }`}>
          {/* Left Side: Projects and Uploads */}
          <div className={`column-container left-column flex flex-col justify-between ${isMobileMode ? 'w-full md:w-full' : 'w-[100vw] md:w-1/2'} h-full shrink-0 border-r border-[#2B2B2B] bg-slate-950/20 overflow-hidden relative min-w-0`}>
          <div className="upper-content-wrapper upper-column-scroll flex-1 overflow-hidden flex flex-col min-h-0 w-full min-w-0">
            {/* Top Control Bar: Autosave text + Expand/Collapse Details right in the same line */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800/80 bg-slate-950/90 shrink-0 w-full min-w-0">
              <div className="flex items-center min-w-0">
                <AutoSaveIndicator isSaving={isSaving} isError={isError} lastSavedAt={lastSavedAt} />
              </div>
              {(isMobileMode || isMobileViewport) && (
                <button
                  type="button"
                  onClick={() => hasProject && setIsHeaderCollapsed(!isHeaderCollapsed)}
                  disabled={!hasProject}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all shadow-sm shrink-0 ml-2 ${
                    !hasProject
                      ? 'opacity-50 cursor-not-allowed bg-slate-900 border-gray-600 text-gray-500'
                      : 'bg-slate-900 border-slate-800 hover:border-emerald-500/50 text-slate-300 hover:text-emerald-400'
                  }`}
                >
                  <span>{isHeaderCollapsed ? 'Expand Details' : 'Collapse Details'}</span>
                  {isHeaderCollapsed ? (
                    <span className="text-[10px] text-emerald-400 font-bold">▼</span>
                  ) : (
                    <span className="text-[10px] text-slate-400 font-bold">▲</span>
                  )}
                </button>
              )}
            </div>

            {/* Company/City and Project Sections Container with smooth transition/animation */}
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden shrink-0 ${
                (isMobileMode || isMobileViewport) && isHeaderCollapsed
                  ? 'max-h-0 opacity-0 border-b-0 pointer-events-none'
                  : 'max-h-[700px] opacity-100 border-b border-slate-800'
              }`}
            >
              {/* Company & City Section Above Project Section */}
              <div className="p-4 border-b border-slate-800/60 bg-slate-900/10">
            <div className="grid grid-cols-2 gap-2.5 w-full">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Company</label>
                <input
                  type="text"
                  placeholder="Enter company name..."
                  value={companyName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCompanyName(val);
                    if (val.trim()) setFieldValidationErrors(prev => ({ ...prev, company: false }));
                    localStorage.setItem('hitec_company_name', val);
                    if (selectedProject) {
                      setSelectedProject(prev => ({ ...prev, company_name: val }));
                      setProjects(prev => prev.map(p => p.id === selectedProject.id ? { ...p, company_name: val } : p));
                      autosave({ company_name: val, company: val });
                    }
                  }}
                  className={`w-full rounded-xl border px-3 py-2 text-xs text-slate-200 placeholder-slate-500 outline-none transition-colors ${
                    fieldValidationErrors.company
                      ? 'border-red-500 bg-red-950/20 focus:border-red-400'
                      : 'border-slate-800 bg-slate-900 focus:border-emerald-500'
                  }`}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">City</label>
                <input
                  type="text"
                  placeholder="Enter city..."
                  value={cityName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCityName(val);
                    if (val.trim()) setFieldValidationErrors(prev => ({ ...prev, city: false }));
                    localStorage.setItem('hitec_city_name', val);
                    if (selectedProject) {
                      setSelectedProject(prev => ({ ...prev, city_name: val }));
                      setProjects(prev => prev.map(p => p.id === selectedProject.id ? { ...p, city_name: val } : p));
                      autosave({ city_name: val, city: val });
                    }
                  }}
                  className={`w-full rounded-xl border px-3 py-2 text-xs text-slate-200 placeholder-slate-500 outline-none transition-colors ${
                    fieldValidationErrors.city
                      ? 'border-red-500 bg-red-950/20 focus:border-red-400'
                      : 'border-slate-800 bg-slate-900 focus:border-emerald-500'
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Project Section */}
          <div className="p-4 border-b border-slate-800 bg-slate-900/10 shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Project</h2>
            </div>

            {/* Create Project Form (Moved up inside section) */}
            <form onSubmit={handleCreateProject} className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder="New project name..."
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-200 placeholder-slate-500 outline-none focus:border-emerald-500 transition-colors"
              />
              <button
                type="submit"
                className="flex items-center gap-1 rounded-xl bg-slate-900 border border-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                Create
              </button>
            </form>
            
            {/* Project Select Dropdown + Rename/Delete Options */}
            <div className="flex gap-2 items-center">
              <select
                value={selectedProject?.id || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const proj = val ? projects.find(p => p.id === val) : null;
                  setSelectedProject(proj);
                  if (proj && proj.company_name) {
                    setCompanyName(proj.company_name);
                    localStorage.setItem('hitec_company_name', proj.company_name);
                  }
                  if (proj && proj.city_name) {
                    setCityName(proj.city_name);
                    localStorage.setItem('hitec_city_name', proj.city_name);
                  }
                }}
                className="flex-1 rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none focus:border-emerald-500 transition-colors"
              >
                {loadingProjects ? (
                  <option value="">Loading projects...</option>
                ) : projects.length === 0 ? (
                  <option value="">No projects. Create one above ↑</option>
                ) : (
                  <option value="">-- Select Project --</option>
                )}
                {!loadingProjects && projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              {selectedProject && (
                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={handleRenameProject}
                    title="Rename current project"
                    className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteProject}
                    title="Delete current project"
                    className="p-2 rounded-xl bg-slate-900 border border-slate-800 text-rose-500 hover:bg-rose-950/20 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
            </div>

          {/* Clean Drag and Drop UploadZone (2 buttons) */}
          <UploadZone
            onFilesSelected={handleFilesSelected}
            photosUsed={dailyUploadCount}
            photosLimit={planLimits.maxDaily}
            isMobileMode={isMobileMode}
            isCompressing={isCompressing}
            isLocked={!hasProject}
          />

          {/* Queue List */}
          <div className="photo-list-container flex-1 overflow-y-auto px-3 py-2 flex flex-col min-h-0 gap-2 left-column-scroll w-full min-w-0">
            <div className="flex items-center justify-between pb-2 sticky top-0 bg-slate-950 z-10 pt-2 border-b border-slate-900">
              {/* LEFT: Select All */}
              <div className="flex items-center gap-2">
                {doneItems.length > 0 && (
                  <label className="flex items-center gap-1.5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={allDoneSelected}
                      onChange={handleSelectAll}
                      className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 accent-emerald-500 cursor-pointer"
                    />
                    <span className="text-[10px] font-semibold text-slate-400 group-hover:text-slate-200 transition-colors">
                      Select all
                    </span>
                  </label>
                )}
              </div>

              {/* MIDDLE: Remove button in RED */}
              <div className="flex items-center justify-center">
                {queue.length > 0 && (
                  <button 
                    onClick={handleRemove}
                    disabled={!allDoneSelected && selectedPhotos.length === 0}
                    className="flex items-center gap-1 text-[11px] font-bold text-rose-500 hover:text-rose-400 bg-rose-950/30 hover:bg-rose-950/50 border border-rose-900/50 px-2.5 py-1 rounded-lg transition-colors disabled:opacity-40 disabled:hover:text-rose-500 disabled:hover:bg-rose-950/30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>

              {/* RIGHT: Selected & Total photos count */}
              <div className="text-[11px] font-semibold text-slate-400">
                {queue.length > 0 ? `${selectedPhotos.length} selected / ${queue.length} photos` : ''}
              </div>
            </div>

            {queue.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-600">
                <FileText className="h-8 w-8 stroke-1 mb-2 text-gray-500" />
                <p className="text-xs text-gray-400">
                  {hasProject ? 'No files queued for upload' : 'Create a project above to start uploading ↑'}
                </p>
              </div>
            ) : (
              <div className={`mt-2 grid gap-1.5 ${queue.length > 20 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
                {queue.filter(item => item.status !== 'Rejected').map((item, index) => {
                  const isDone = item.status === 'Done';
                  const isSelected = selectedPhotos.includes(item.finalFilename);
                  const matchedPhoto = projectPhotos.find(p => p.filename === item.finalFilename);
                  const currentCaption = matchedPhoto ? matchedPhoto.caption : '';
                  const canNavigate = isDone && projectPhotos.some(p => p.filename === item.finalFilename);

                  return (
                    <div
                      key={item.id}
                      draggable={true}
                      onDragStart={() => setDraggedItemIndex(index)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (dragOverItemIndex !== index) setDragOverItemIndex(index);
                      }}
                      onDragLeave={() => {
                        if (dragOverItemIndex === index) setDragOverItemIndex(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        handleDropQueueItem(index);
                      }}
                      onDragEnd={() => {
                        setDraggedItemIndex(null);
                        setDragOverItemIndex(null);
                      }}
                      className={`flex items-center gap-2 rounded-xl border p-2.5 transition-all cursor-grab active:cursor-grabbing w-full min-w-0 overflow-hidden ${
                        dragOverItemIndex === index
                          ? 'border-emerald-400 bg-emerald-950/40 scale-[1.01] shadow-lg'
                          : draggedItemIndex === index
                            ? 'opacity-40 border-dashed border-slate-700'
                            : projectPhotos[editorIndex]?.filename === item.finalFilename
                              ? 'border-yellow-500/60 bg-yellow-950/20'
                              : isSelected
                                ? 'border-emerald-500/60 bg-emerald-950/20'
                                : 'border-slate-900 bg-slate-900/30'
                      } ${!isDone ? 'opacity-60' : ''}`}
                    >
                      {/* LEFT: Row number */}
                      <div
                        className="shrink-0 flex items-center pl-1 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePhotoSelection(item.finalFilename, item.status);
                          if (canNavigate) navigateToPhoto(item.finalFilename);
                        }}
                      >
                        <span className="text-xs font-mono font-semibold text-slate-400 min-w-[20px] text-right">
                          {index + 1}.
                        </span>
                      </div>

                      {/* CENTER: Thumbnail + info or PhotoItem for Hold-To-Talk voice captioning */}
                      {isDone && matchedPhoto ? (
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <PhotoItem 
                            photo={matchedPhoto} 
                            onUpdateCaption={handleUpdateCaptionFromVoice} 
                            onSelectPhoto={() => {
                              togglePhotoSelection(item.finalFilename, item.status);
                              if (canNavigate) navigateToPhoto(item.finalFilename);
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-2.5 min-w-0 flex-1 cursor-pointer group overflow-hidden"
                          onClick={() => {
                            togglePhotoSelection(item.finalFilename, item.status);
                            if (canNavigate) navigateToPhoto(item.finalFilename);
                          }}
                          title="Click to select and view in Caption Editor"
                        >
                          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-slate-800 bg-slate-950 flex items-center justify-center">
                            {item.thumbnailUrl ? (
                              <img
                                src={item.thumbnailUrl}
                                alt="thumbnail"
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <FileText className="h-4 w-4 text-slate-500" />
                            )}
                            {canNavigate && (
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg">
                                <ImageIcon className="h-4 w-4 text-white" />
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 flex-1 overflow-hidden">
                            <p className="truncate block w-full min-w-0 text-xs font-medium text-slate-200 group-hover:text-emerald-300 transition-colors">{item.finalFilename}</p>
                            <p className="text-[10px] text-slate-500">{Math.round(item.sizeKb)} KB</p>
                            <p className={`text-[10px] truncate block w-full min-w-0 mt-0.5 ${currentCaption ? 'text-emerald-400 font-medium' : 'text-slate-600 italic'}`}>
                              {currentCaption || 'No caption'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* RIGHT: Status chip */}
                      <div className="shrink-0 flex items-center gap-1.5 pl-1">
                        {item.status === 'Queued' && (
                          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 animate-pulse">Queued</span>
                        )}
                        {item.status === 'Uploading' && (
                          <div className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin text-emerald-400" />
                            <span className="text-[10px] font-bold text-emerald-400">{item.progress}%</span>
                          </div>
                        )}
                        {item.status === 'Retrying' && (
                          <div className="flex items-center gap-1">
                            <RefreshCw className="h-3 w-3 animate-spin text-amber-400" />
                            <span className="text-[10px] font-bold text-amber-400">{item.retries}/3</span>
                          </div>
                        )}
                        {item.status === 'Done' && (
                          <div className="flex items-center gap-1.5">
                            {isSelected && (
                              <Check className="h-4 w-4 text-white stroke-[3] shrink-0" title="Selected" />
                            )}
                            {!isMobileMode && projectPhotos[editorIndex]?.filename === item.finalFilename && (
                              <Edit2 className="h-4 w-4 text-yellow-400 shrink-0 animate-pulse" title="Currently viewing in Caption Editor" />
                            )}
                          </div>
                        )}
                        {item.status === 'Rejected' && (
                          <div className="group/tip relative">
                            <AlertCircle className="h-4 w-4 text-rose-400 cursor-pointer" />
                            <div className="absolute right-0 bottom-full mb-1 hidden w-44 rounded bg-slate-900 border border-slate-800 p-2 text-[10px] text-rose-300 group-hover/tip:block z-20 shadow-xl">{item.error}</div>
                          </div>
                        )}
                        {item.status === 'Blocked' && (
                          <div className="group/tip relative">
                            <AlertCircle className="h-4 w-4 text-amber-500 cursor-pointer" />
                            <div className="absolute right-0 bottom-full mb-1 hidden w-44 rounded bg-slate-900 border border-slate-800 p-2 text-[10px] text-amber-300 group-hover/tip:block z-20 shadow-xl">{item.error}</div>
                          </div>
                        )}

                        {/* RIGHT: 6 white dots drag icon to the right of status icon */}
                        <div
                          className="text-slate-400 hover:text-white transition-colors p-1 cursor-grab active:cursor-grabbing ml-1"
                          title="Drag row to reorder position"
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>

          {/* Action Toolbar Relocated to Left Column (Confirm, PowerPoint, Word, Excel) */}
          <PublishBar
            confirmCount={selectedPhotos.length}
            isConfirmed={confirmedExports}
            onExport={handlePublishBarExport}
            isLocked={!hasProject}
            isSaving={isSaving}
          />
          </div>


          {/* Right Side: Photo Carousel Editor and Exporters */}

          {!isMobileMode && (
          <div className="column-container right-column w-[100vw] md:w-1/2 h-full shrink-0 flex flex-col justify-between overflow-hidden bg-slate-900/10 relative">
            {/* Photo Editor Tab View / Main Pane */}
            <div className="flex-1 flex flex-col overflow-hidden p-3 md:p-4 min-h-0 w-full min-w-0">
            {!selectedProject ? (
              <div className="flex-1 flex flex-col items-center justify-center rounded-2xl border border-slate-900 bg-slate-900/20 p-8 text-center text-slate-600">
                <Folder className="h-10 w-10 stroke-1 mb-2 text-slate-500" />
                <h3 className="font-semibold text-slate-400 text-sm">No Project Selected</h3>
                <p className="mt-1 text-xs text-slate-500 max-w-xs">
                  Please select or create a project first to edit captions and view uploaded photos.
                </p>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden gap-1.5 min-h-0" style={{display:'flex',flexDirection:'column'}}>
                {projectPhotos.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center rounded-2xl border border-slate-900 bg-slate-900/20 p-8 text-center text-slate-600 min-h-0">
                    <ImageIcon className="h-10 w-10 stroke-1 mb-2" />
                    <h3 className="font-semibold text-slate-400 text-sm">No uploaded photos</h3>
                    <p className="mt-1 text-xs text-slate-500 max-w-xs">
                      Upload photos for this project to start editing captions. Only successfully validated photos will show here.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Canvas — grows to fill all available vertical space */}
                    <div className="w-full relative flex flex-col group overflow-hidden" style={{flex:'1 1 0',minHeight:'140px'}}>
                      <div className="w-full h-full relative flex flex-col overflow-hidden">
                        <AnnotatedImageCanvas
                          photo={projectPhotos[editorIndex]}
                          onSaveAnnotatedImage={handleSaveAnnotatedImage}
                          photoCounter={`${editorIndex + 1} / ${projectPhotos.length}`}
                        />

                        {/* Left arrow overlay */}
                        <button
                          type="button"
                          disabled={editorIndex === 0}
                          onClick={() => setEditorIndex(prev => prev - 1)}
                          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-slate-800 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-md"
                          title="Previous photo"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>

                        {/* Right arrow overlay */}
                        <button
                          type="button"
                          disabled={editorIndex === projectPhotos.length - 1}
                          onClick={() => setEditorIndex(prev => prev + 1)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-slate-800 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white transition-all disabled:opacity-30 disabled:pointer-events-none shadow-md"
                          title="Next photo"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col overflow-y-auto gap-1.5 pr-0.5 shrink-0" style={{maxHeight:'55%'}}>
                      {/* COMPACT METADATA CARD (Flush below canvas without blur auto-saves) */}
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-2.5 shadow-sm shrink-0">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {/* Caption */}
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                              Caption
                            </label>
                            <input
                              type="text"
                              value={photoTitle}
                              onKeyDown={(e) => e.stopPropagation()}
                              onKeyUp={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPhotoTitle(val);
                                updatePhotoFieldAndAutosave({ caption: val, title: val, asset_title: val });
                              }}
                              placeholder="Add or edit caption..."
                              className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-1 text-xs text-white font-medium placeholder-slate-600 focus:border-emerald-500 focus:outline-none transition-colors"
                            />
                          </div>

                          {/* Inspection Date */}
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                              Date
                            </label>
                            <input
                              type="date"
                              value={photoDate}
                              onKeyDown={(e) => e.stopPropagation()}
                              onKeyUp={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPhotoDate(val);
                                updatePhotoFieldAndAutosave({ date: val, exif_date: val });
                              }}
                              className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-1 text-xs text-white font-medium placeholder-slate-600 focus:border-emerald-500 focus:outline-none transition-colors"
                            />
                          </div>

                          {/* Location */}
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                              Location
                            </label>
                            <input
                              type="text"
                              value={photoLocation}
                              onKeyDown={(e) => e.stopPropagation()}
                              onKeyUp={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const val = e.target.value;
                                setPhotoLocation(val);
                                updatePhotoFieldAndAutosave({ location: val, exif_gps: val });
                              }}
                              placeholder="e.g. Electrical Room A"
                              className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-1 text-xs text-white font-medium placeholder-slate-600 focus:border-emerald-500 focus:outline-none transition-colors"
                            />
                          </div>
                        </div>
                      </div>

                      {/* GRADES PRIORITY & LATEST STATUS CONTROL ROW */}
                      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-2 px-2.5 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-2 shrink-0">
                        {/* Grades Priority */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 shrink-0">
                            <ShieldAlert className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                            <span>Grades:</span>
                          </label>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {[
                              {
                                id: 'F1',
                                label: 'F1',
                                activeClass: 'bg-red-500 text-white border-red-500 shadow-md shadow-red-500/25 font-bold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-red-400 border-slate-800 hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-300 font-medium'
                              },
                              {
                                id: 'F2',
                                label: 'F2',
                                activeClass: 'bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/25 font-bold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-orange-400 border-slate-800 hover:border-orange-500/50 hover:bg-orange-500/10 hover:text-orange-300 font-medium'
                              },
                              {
                                id: 'F3',
                                label: 'F3',
                                activeClass: 'bg-yellow-500 text-slate-950 border-yellow-400 shadow-md shadow-yellow-500/25 font-extrabold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-yellow-400 border-slate-800 hover:border-yellow-500/50 hover:bg-yellow-500/10 hover:text-yellow-300 font-medium'
                              },
                              {
                                id: 'F4',
                                label: 'F4',
                                activeClass: 'bg-emerald-600 text-white border-emerald-500 shadow-md shadow-emerald-500/25 font-bold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-emerald-400 border-slate-800 hover:border-emerald-500/50 hover:bg-emerald-500/10 hover:text-emerald-300 font-medium'
                              }
                            ].map((item) => {
                              const isChecked = photoGrade === item.id || photoGrade?.startsWith(item.id);
                              return (
                                <label
                                  key={item.id}
                                  className={`inline-flex items-center justify-center cursor-pointer px-2.5 py-1 rounded-lg border text-xs transition-all duration-200 ease-in-out active:scale-95 select-none ${
                                    isChecked ? item.activeClass : item.inactiveClass
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="gradesPriority"
                                    value={item.id}
                                    checked={isChecked}
                                    onChange={() => {
                                      setPhotoGrade(item.id);
                                      updatePhotoFieldAndAutosave({ grade: item.id, assessment_grade: item.id });
                                    }}
                                    className="sr-only"
                                  />
                                  <span>{item.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>

                        {/* Latest Status */}
                        <div className="flex items-center gap-2 sm:border-l sm:border-slate-800 sm:pl-3 flex-wrap">
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider shrink-0">
                            Status:
                          </span>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {[
                              {
                                id: 'Open',
                                label: 'Open',
                                isChecked: !photoStatus || photoStatus === 'Open',
                                activeClass: 'bg-emerald-600 text-white border-emerald-500 shadow-md shadow-emerald-500/25 font-bold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-emerald-400 border-slate-800 hover:border-emerald-500/50 hover:bg-emerald-500/10 hover:text-emerald-300 font-medium'
                              },
                              {
                                id: 'Close',
                                label: 'Close',
                                isChecked: photoStatus === 'Close',
                                activeClass: 'bg-rose-600 text-white border-rose-500 shadow-md shadow-rose-500/25 font-bold scale-[1.03]',
                                inactiveClass: 'bg-slate-950/80 text-rose-400 border-slate-800 hover:border-rose-500/50 hover:bg-rose-500/10 hover:text-rose-300 font-medium'
                              }
                            ].map((statusItem) => (
                              <label
                                key={statusItem.id}
                                className={`inline-flex items-center justify-center cursor-pointer px-2.5 py-1 rounded-lg border text-xs transition-all duration-200 ease-in-out active:scale-95 select-none ${
                                  statusItem.isChecked ? statusItem.activeClass : statusItem.inactiveClass
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="latestStatus"
                                  value={statusItem.id}
                                  checked={statusItem.isChecked}
                                  onChange={() => {
                                    setPhotoStatus(statusItem.id);
                                    updatePhotoFieldAndAutosave({ status: statusItem.id, latest_status: statusItem.id });
                                  }}
                                  className="sr-only"
                                />
                                <span>{statusItem.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* CARD A: COMMENTS / KOMENTAR */}
                      <div className="shrink-0 flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-2 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between mb-1 shrink-0">
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-bold text-white uppercase tracking-wide">
                              {commentsLang === 'ID' ? 'KOMENTAR' : 'COMMENTS'}
                            </label>
                            <button
                              type="button"
                              onClick={() => setAiAssistOn(!aiAssistOn)}
                              className={`rounded-full px-2 py-0.5 text-[10px] font-bold border transition-colors flex items-center gap-1 shadow-sm ${
                                aiAssistOn ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-slate-800 border-slate-700 text-slate-400'
                              }`}
                              title="Toggle AI Auto-Complete & Suggestion Pills"
                            >
                              <Sparkles className="h-3 w-3" />
                              <span>AI Assist: {aiAssistOn ? 'ON' : 'OFF'}</span>
                            </button>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {cardASpeech.supported && (
                              <button
                                type="button"
                                onPointerDown={(e) => { e.stopPropagation(); cardASpeech.start(); }}
                                onPointerUp={(e) => { e.stopPropagation(); cardASpeech.stop(); }}
                                onPointerLeave={(e) => { e.stopPropagation(); cardASpeech.stop(); }}
                                style={{ touchAction: 'manipulation' }}
                                className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold transition-all shrink-0 ${
                                  cardASpeech.isRecording
                                    ? 'bg-red-500 text-white scale-105 shadow-lg shadow-red-500/30'
                                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700'
                                }`}
                                title="Press and hold to talk"
                              >
                                <MicIcon className={`w-3.5 h-3.5 ${cardASpeech.isRecording ? 'text-white animate-pulse' : 'text-slate-400'}`} />
                                <span>{cardASpeech.isRecording ? `Listening... ${cardASpeech.detectedLang === 'id-ID' ? 'ID' : 'EN'}` : 'Hold to Talk'}</span>
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => {
                                setCommentsText('');
                                updatePhotoFieldAndAutosave({ comments_text: '', comments: '' });
                              }}
                              title="Clear"
                              className="flex items-center gap-1 rounded-lg bg-rose-600/80 hover:bg-rose-500 text-white px-2 py-0.5 text-[10px] font-bold transition-colors shadow-sm shrink-0"
                            >
                              <X className="h-3 w-3" />
                              <span>Clear</span>
                            </button>
                          </div>
                        </div>

                        <textarea
                          rows={2}
                          style={{ height: 'auto', minHeight: '44px' }}
                          value={commentsText}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          onChange={(e) => {
                            const lines = e.target.value.split('\n');
                            let newText = e.target.value;
                            if (lines.length <= 3) {
                              setCommentsText(newText);
                            } else {
                              newText = lines.slice(0, 3).join('\n');
                              setCommentsText(newText);
                            }
                            updatePhotoFieldAndAutosave({ comments_text: newText, comments: newText });
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          placeholder={commentsLang === 'ID' ? 'Tulis komentar observasi...' : 'Write observation comments...'}
                          className="comments-textarea w-full rounded-xl border border-slate-800 bg-slate-950 px-2.5 py-1.5 text-xs text-white font-medium outline-none focus:border-emerald-500 placeholder-slate-600 transition-colors resize-y leading-relaxed overflow-y-auto"
                        />

                        {aiAssistOn && aiSuggestions.length > 0 && (
                          <div className="mt-1 pt-1 border-t border-slate-800/80 shrink-0">
                            <div className="flex flex-wrap gap-1 items-center">
                              <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-0.5 shrink-0">
                                <Sparkles className="h-2.5 w-2.5" /> Suggestions:
                              </span>
                              {aiSuggestions.map((pill, pIdx) => (
                                <button
                                  key={pIdx}
                                  type="button"
                                  onClick={() => {
                                    const newLines = commentsText ? (commentsText + '\n' + pill) : pill;
                                    const splitted = newLines.split('\n').slice(0, 3).join('\n');
                                    setCommentsText(splitted);
                                    updatePhotoFieldAndAutosave({ comments_text: splitted, comments: splitted });
                                    const ta = document.querySelector('.comments-textarea');
                                    if (ta) {
                                      ta.style.height = 'auto';
                                      ta.style.height = `${ta.scrollHeight}px`;
                                    }
                                  }}
                                  className="rounded-lg bg-slate-950 hover:bg-emerald-950/40 border border-slate-800 hover:border-emerald-500/50 px-2 py-0.5 text-left text-[10px] font-medium text-slate-300 hover:text-emerald-300 transition-all max-w-full truncate"
                                  title={pill}
                                >
                                  + {pill}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* CARD B: ASSESSOR RECOMMENDATION - AI Fire Safety Assessor */}
                      <div className="shrink-0 flex flex-col rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-2 shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between flex-wrap gap-1.5 mb-1 shrink-0">
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-bold text-white uppercase tracking-wide">
                              {commentsLang === 'ID' ? 'REKOMENDASI AI' : 'AI RECOMMENDATION'}
                            </label>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold border flex items-center gap-1 shadow-sm ${
                              recMode === 'Auto' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                            }`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${recMode === 'Auto' ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                              <span>{recMode === 'Auto' ? 'Auto' : 'Manual Override'}</span>
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setRecommendations([]);
                              updatePhotoFieldAndAutosave({ recommendations_json: [] });
                            }}
                            title="Clear"
                            className="flex items-center gap-1 rounded-lg bg-rose-600/80 hover:bg-rose-500 text-white px-2 py-0.5 text-[10px] font-bold transition-colors shadow-sm shrink-0"
                          >
                            <X className="h-3 w-3" />
                            <span>Clear</span>
                          </button>
                        </div>

                        <textarea
                          rows={2}
                          style={{ height: 'auto', minHeight: '44px' }}
                          value={Array.isArray(recommendations) ? recommendations.join('\n') : (recommendations || '')}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                          onInput={(e) => {
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          onChange={(e) => {
                            const lines = e.target.value.split('\n');
                            let newRecs;
                            if (lines.length <= 3) {
                              newRecs = lines;
                              setRecommendations(lines);
                            } else {
                              newRecs = lines.slice(0, 3);
                              setRecommendations(newRecs);
                            }
                            if (recMode === 'Auto') setRecMode('Manual');
                            updatePhotoFieldAndAutosave({ recommendations_json: newRecs });
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          placeholder={commentsLang === 'ID' ? 'Rekomendasi tindakan mitigasi...' : 'Mitigation recommendation actions...'}
                          className="ai-recommendation-textarea w-full rounded-xl border border-slate-800 bg-slate-950 px-2.5 py-1.5 text-xs text-white font-medium outline-none focus:border-emerald-500 placeholder-slate-600 transition-colors resize-y leading-relaxed overflow-y-auto"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Sticky right-column bottom footer aligned horizontally and vertically with left-column's PublishBar */}
          {selectedProject && projectPhotos.length > 0 && (
            <footer
              className="right-publish-bar w-full border-t border-slate-800 bg-[#020617] shrink-0 px-3 flex items-center justify-between gap-2 z-50 sticky bottom-0 left-0 right-0 shadow-2xl overflow-hidden"
              style={{ position: 'sticky', bottom: 0, height: '63px', minHeight: '63px', maxHeight: '63px', backgroundColor: '#020617', zIndex: 50, flexShrink: 0 }}
            >
              {/* Left side: Regenerate & Edit Manually */}
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={handleGenerateRecommendation}
                  disabled={aiGeneratingRec}
                  className="h-[38px] flex items-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-2.5 text-xs font-bold text-white transition-all disabled:opacity-50 shadow-md shrink-0"
                  title="Regenerate recommendation based on current comment and grade"
                >
                  {aiGeneratingRec ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Sparkles className="h-4 w-4 shrink-0" />}
                  <span>{commentsLang === 'ID' ? 'Regenerate' : 'Regenerate'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setRecMode(prev => prev === 'Auto' ? 'Manual' : 'Auto')}
                  className={`h-[38px] flex items-center gap-1.5 rounded-xl border px-2.5 text-xs font-bold transition-all shadow-md shrink-0 ${
                    recMode === 'Manual'
                      ? 'border-amber-500/50 bg-amber-500/10 text-amber-300 hover:bg-amber-500 hover:text-white'
                      : 'border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-700 hover:text-white'
                  }`}
                  title="Toggle between Auto-fill and Manual Override mode"
                >
                  <span>{recMode === 'Manual' ? 'Edit Manually (Active)' : 'Edit Manually'}</span>
                </button>
              </div>

              {/* Right side: Bahasa / English & Save Report (#1) */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="h-[38px] flex items-center rounded-xl border border-slate-800 bg-slate-900 p-0.5 shadow-md shrink-0" title="Auto Translate & Grammar Check all texts">
                  <button
                    type="button"
                    onClick={() => handleLanguageSwitch('ID')}
                    className={`h-full px-2.5 flex items-center justify-center text-xs font-bold rounded-lg transition-all ${
                      commentsLang === 'ID' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Bahasa
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLanguageSwitch('EN')}
                    className={`h-full px-2.5 flex items-center justify-center text-xs font-bold rounded-lg transition-all ${
                      commentsLang === 'EN' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    English
                  </button>
                </div>

                {/* #1: Save Report button positioned right next to Bahasa/English toggle */}
                <button
                  type="button"
                  onClick={handleSaveReport}
                  className="h-[38px] flex items-center gap-1.5 rounded-xl bg-[#107C41] hover:bg-[#0C5E31] px-3 text-xs font-bold text-white shadow-md shadow-[#107C41]/25 active:scale-95 transition-all cursor-pointer shrink-0"
                  title="Save all report details and attach attributes to this photo entry in left column list"
                >
                  <Save className="h-4 w-4 shrink-0" />
                  <span>Save Report</span>
                </button>
              </div>
            </footer>
          )}
          </div>
          )}
        </div>
      </div>

      {/* Mobile Tab bar footer (highly compact responsive overlay) */}
      {!isMobileUser && !isMobileMode && (
        <div className="flex md:hidden h-14 shrink-0 border-t border-slate-800 bg-slate-900/90 backdrop-blur-md px-6 items-center justify-around z-20">
          <button
            onClick={() => setActiveTab('upload')}
            className={`flex flex-col items-center justify-center gap-1 text-[10px] font-bold ${
              activeTab === 'upload' ? 'text-emerald-400' : 'text-slate-500'
            }`}
          >
            <Upload className="h-4 w-4" />
            <span>Upload</span>

        </button>

        <button
          onClick={() => setActiveTab('editor')}
          className={`flex flex-col items-center justify-center gap-1 text-[10px] font-bold ${
            activeTab === 'editor' ? 'text-emerald-400' : 'text-slate-500'
          }`}
        >
          <ImageIcon className="h-4 w-4" />
          <span>Editor</span>
        </button>

        <button
          onClick={() => setActiveTab('export')}
          className={`flex flex-col items-center justify-center gap-1 text-[10px] font-bold ${
            activeTab === 'export' ? 'text-emerald-400' : 'text-slate-500'
          }`}
        >
          <FileText className="h-4 w-4" />
          <span>Export</span>
        </button>
      </div>
      )}

      {/* Alert / Popup Message Modal */}

      {alertPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                alertPopup.type === 'error'
                  ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                  : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
              }`}>
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{alertPopup.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{alertPopup.message}</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setAlertPopup(null)}
                className="rounded-xl bg-slate-800 px-5 py-2.5 text-xs font-semibold text-slate-200 hover:bg-slate-700 transition-colors"
              >
                Got It
              </button>
              {alertPopup.showUpgradeButton && (
                <button
                  onClick={() => {
                    setAlertPopup(null);
                    setShowUpgradeModal(true);
                  }}
                  className="rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-600/30"
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Connectivity Interceptor Modal */}
      {connectivityModal && connectivityModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-sm rounded-2xl bg-slate-900 border border-slate-800 p-6 shadow-2xl text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-500">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">No Internet Connection</h3>
              <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                Unable to connect to the cloud server or share files. Please check your network connectivity and try again.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setConnectivityModal(null)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = connectivityModal.action;
                  setConnectivityModal(null);
                  if (typeof navigator !== 'undefined' && !navigator.onLine) {
                    setTimeout(() => setConnectivityModal({ show: true, action }), 150);
                  } else if (action) {
                    action();
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-xs font-bold text-white shadow-lg shadow-emerald-600/30 transition-all active:scale-95"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Share Action Sheet / Modal */}
      {mobileShareModal && mobileShareModal.show && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-sm rounded-2xl bg-slate-900 border border-slate-800 p-5 shadow-2xl space-y-4 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-base font-bold text-white">Share {mobileShareModal.type} Report</h3>
                <p className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[240px]">
                  {mobileShareModal.fileName || 'Inspection Report'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMobileShareModal(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2.5 pt-1">
              <button
                type="button"
                onClick={handleWhatsAppShare}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-emerald-400 font-semibold text-xs transition-all active:scale-95"
              >
                <div className="w-8 h-8 rounded-lg bg-[#25D366] flex items-center justify-center text-white shrink-0 shadow-md">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-white">Share to WhatsApp</div>
                  <div className="text-[10px] text-slate-400 font-normal">Send directly to contacts or groups</div>
                </div>
              </button>

              <button
                type="button"
                onClick={handleGoogleDriveShare}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-400 font-semibold text-xs transition-all active:scale-95"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white shrink-0 shadow-md">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.808 3.138 3.995 6.928l3.978 6.892h7.661l.088-.135c.048-.076 1.765-3.046 3.816-6.602l3.729-6.467-.113-.082c-.062-.046-1.545-.066-8.68-.117l-8.583-.066-.148.602zM7.173 2.766L.071 15.06l3.816 6.611 3.816 6.61 7.106-12.298c3.908-6.764 7.106-12.308 7.106-12.32 0-.012-3.238-.035-7.195-.051l-7.193-.03-3.854 6.669-.5 4.515z" />
                  </svg>
                </div>
                <div className="text-left flex-1">
                  <div className="font-bold text-white">Google Drive</div>
                  <div className="text-[10px] text-slate-400 font-normal">Upload and save to cloud drive</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradeModal 
        open={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
        currentLimit={planLimits.maxDaily} 
      />
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        user={user}
        isPro={isPro}
        lang="EN"
      />
    </div>
  );
}
